import React, { useState, useRef, useEffect } from 'react';
import { Mic, Upload, Settings, Download, Play, Pause, Trash2, Check, FileAudio, Clock, Info, ExternalLink, Globe, Circle } from 'lucide-react';

// ============================================================================
// FILE: components/StoriesTab.js
// ============================================================================
function StoriesTab({ stories, selectedStory, onSelectStory, onRunStory, isRunning, config, comparisonResults, credentials }) {
  const [filter, setFilter] = useState('all');

  const filteredStories = stories.filter(story => {
    if (filter === 'all') return true;
    if (filter === 'custom') return story.compare_models?.some(m => !m.base_model);
    if (filter === 'multispeaker') return story.multispeaker;
    return true;
  });

  const hasComparison = selectedStory?.compare_models?.length > 1;
  
  // Always allow running stories since we use proxy
  const canRunStory = true;

  return (
    <div className="flex gap-4">
      {/* Left Column - Story Selection (scrollable, fixed width) */}
      <div className="w-80 flex-shrink-0">
        <div className="bg-white rounded-lg shadow-md p-4 sticky top-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-900">Use Case Stories</h2>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Stories</option>
              <option value="custom">Custom Models</option>
              <option value="multispeaker">Multi-Speaker</option>
            </select>
          </div>

          {filteredStories.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <FileAudio className="w-10 h-10 mx-auto mb-2" />
              <p className="text-xs">No stories available</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
              {filteredStories.map(story => (
                <div
                  key={story.id}
                  onClick={() => onSelectStory(story)}
                  className={`border-2 rounded-lg p-3 transition-all cursor-pointer ${
                    selectedStory?.id === story.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center space-x-1.5 flex-1 min-w-0">
                      {selectedStory?.id === story.id && (
                        <Check className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                      )}
                      <h3 className="font-semibold text-gray-900 text-xs truncate">{story.name}</h3>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1 mb-2">
                    {story.multispeaker && (
                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 text-[10px] rounded-full">
                        Multi-Speaker
                      </span>
                    )}
                    {story.compare_models?.some(m => !m.base_model) && (
                      <span className="px-1.5 py-0.5 bg-green-100 text-green-800 text-[10px] rounded-full">
                        Custom
                      </span>
                    )}
                  </div>

                  {story.unique_characteristics && story.unique_characteristics.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {story.unique_characteristics.slice(0, 2).map((char, idx) => (
                        <span key={idx} className="px-1.5 py-0.5 bg-gray-100 text-gray-700 text-[10px] rounded">
                          {char}
                        </span>
                      ))}
                      {story.unique_characteristics.length > 2 && (
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded">
                          +{story.unique_characteristics.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Column - Story Details and Results (takes remaining space) */}
      <div className="flex-1 space-y-4">
        {selectedStory ? (
          <>
            {/* Selected Story Details */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">{selectedStory.name}</h3>
              
              {/* Characteristics */}
              {selectedStory.unique_characteristics && selectedStory.unique_characteristics.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-700 mb-1.5">Characteristics:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedStory.unique_characteristics.map((char, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {char}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Story Features */}
              <div className="mb-3 pb-3 border-b border-gray-200">
                <p className="text-xs font-medium text-gray-700 mb-1.5">Enabled Features:</p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedStory.multispeaker && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                      Speaker Diarization
                    </span>
                  )}
                  {selectedStory.smartformatting && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                      Smart Formatting {selectedStory.smartformatting_version ? `v${selectedStory.smartformatting_version}` : ''}
                    </span>
                  )}
                  {selectedStory.enrichment && selectedStory.enrichment !== 'none' && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                      {selectedStory.enrichment.charAt(0).toUpperCase() + selectedStory.enrichment.slice(1)} Enrichment
                    </span>
                  )}
                </div>
              </div>

              {/* Model comparison info */}
              {selectedStory.compare_models && selectedStory.compare_models.length > 1 && (
                <div className="mb-3 pb-3 border-b border-gray-200">
                  <p className="text-xs font-medium text-gray-700 mb-1.5">Comparing Models:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedStory.compare_models.map((model, idx) => (
                      <span key={idx} className={`px-2 py-1 text-xs rounded ${
                        model.base_model 
                          ? 'bg-blue-100 text-blue-800 border border-blue-200'
                          : 'bg-green-100 text-green-800 border border-green-200'
                      }`}>
                        {model.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Audio preview */}
              {selectedStory.audio && selectedStory.audio.file && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-700 mb-1.5">Audio Preview:</p>
                  <audio src={selectedStory.audio.file} controls className="w-full h-8" />
                </div>
              )}

              {/* Run controls */}
              <div className="space-y-2">
                <button
                  onClick={() => onRunStory(selectedStory)}
                  disabled={!canRunStory || isRunning}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center space-x-2"
                >
                  {isRunning ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Running Transcription...</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      <span>Run Story{hasComparison ? ' (Compare Models)' : ''}</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Comparison Results */}
            {comparisonResults && comparisonResults.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-gray-900">Comparison Results</h3>
                  <span className="text-xs text-gray-500">{comparisonResults.length} model(s)</span>
                </div>

                <div className={`grid gap-4 ${comparisonResults.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {comparisonResults.map((result, idx) => (
                    <ComparisonResultPanel
                      key={idx}
                      result={result}
                      story={selectedStory}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <FileAudio className="w-16 h-16 mx-auto mb-3 text-gray-300" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">Select a Story</h3>
            <p className="text-sm text-gray-500">Choose a story from the list on the left to view details and run transcription</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// FILE: components/ComparisonResultPanel.js
// ============================================================================
function ComparisonResultPanel({ result, story }) {
  const [showWordDetails, setShowWordDetails] = useState(false);
  const [showEnrichedResults, setShowEnrichedResults] = useState(true);
  
  // Determine if speaker labels should be shown based on story metadata
  const speakerLabelsEnabled = story?.multispeaker === true;
  
  // Determine if enriched results should be shown
  const hasEnrichedResults = result.enrichedResults && result.enrichedResults.transcript;
  
  console.log('ComparisonResultPanel - hasEnrichedResults:', hasEnrichedResults);
  console.log('ComparisonResultPanel - enrichedResults:', result.enrichedResults);

  const formatTimestamp = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(2);
    return `${mins}:${secs.padStart(5, '0')}`;
  };

  const getConfidenceColor = (confidence) => {
    if (!confidence) return 'bg-gray-100 text-gray-700';
    if (confidence >= 0.9) return 'bg-green-100 text-green-800';
    if (confidence >= 0.75) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getSpeakerColor = (speaker) => {
    const colors = [
      'border-blue-500 bg-blue-50',
      'border-green-500 bg-green-50',
      'border-purple-500 bg-purple-50',
      'border-orange-500 bg-orange-50',
      'border-pink-500 bg-pink-50',
      'border-indigo-500 bg-indigo-50',
    ];
    if (!speaker || typeof speaker !== 'number') return 'border-gray-400 bg-gray-50';
    return colors[speaker % colors.length];
  };

  const mergeResultsWithSpeakers = () => {
    if (!result.transcripts || result.transcripts.length === 0) return [];

    const speakerLabels = result.speakerLabels || [];
    const hasSpeakerLabels = speakerLabelsEnabled && speakerLabels.length > 0;

    // If we have speaker labels, we need to segment by speaker changes
    if (hasSpeakerLabels && speakerLabels.length > 0) {
      const merged = [];
      
      // Collect all words with their timestamps
      const allWords = [];
      result.transcripts.forEach(transcript => {
        const timestamps = transcript.alternatives?.[0]?.timestamps;
        if (timestamps && timestamps.length > 0) {
          timestamps.forEach(wordData => {
            allWords.push({
              word: wordData[0],
              startTime: wordData[1],
              endTime: wordData[2]
            });
          });
        }
      });

      if (allWords.length === 0) return merged;

      // Group words by speaker
      let currentSpeaker = null;
      let currentSegment = [];
      let currentStartTime = null;
      let currentEndTime = null;

      allWords.forEach(wordData => {
        // Find which speaker this word belongs to
        const speakerLabel = speakerLabels.find(label => 
          wordData.startTime >= label.from && wordData.startTime < label.to
        );
        
        const speaker = speakerLabel ? speakerLabel.speaker : null;

        // If speaker changed, save current segment and start new one
        if (speaker !== currentSpeaker && currentSegment.length > 0) {
          merged.push({
            transcript: currentSegment.map(w => w.word).join(' '),
            startTime: currentStartTime,
            endTime: currentEndTime,
            confidence: null,
            timestamps: currentSegment.map(w => [w.word, w.startTime, w.endTime]),
            speaker: currentSpeaker,
            key: merged.length
          });
          currentSegment = [];
          currentStartTime = null;
        }

        // Add word to current segment
        if (currentStartTime === null) {
          currentStartTime = wordData.startTime;
        }
        currentEndTime = wordData.endTime;
        currentSpeaker = speaker;
        currentSegment.push(wordData);
      });

      // Add final segment
      if (currentSegment.length > 0) {
        merged.push({
          transcript: currentSegment.map(w => w.word).join(' '),
          startTime: currentStartTime,
          endTime: currentEndTime,
          confidence: null,
          timestamps: currentSegment.map(w => [w.word, w.startTime, w.endTime]),
          speaker: currentSpeaker,
          key: merged.length
        });
      }

      return merged;
    }

    // No speaker labels - use original transcripts
    const merged = [];
    
    result.transcripts.forEach((transcript, idx) => {
      const text = transcript.alternatives?.[0]?.transcript || '';
      const timestamps = transcript.alternatives?.[0]?.timestamps;
      const confidence = transcript.alternatives?.[0]?.confidence;
      
      if (!timestamps || timestamps.length === 0) {
        merged.push({
          transcript: text,
          startTime: null,
          endTime: null,
          confidence,
          timestamps: [],
          speaker: null,
          key: idx
        });
        return;
      }

      const startTime = timestamps[0][1];
      const endTime = timestamps[timestamps.length - 1][2];
      
      merged.push({
        transcript: text,
        startTime,
        endTime,
        confidence,
        timestamps,
        speaker: null,
        key: idx
      });
    });

    return merged;
  };

  const mergedResults = mergeResultsWithSpeakers();
  const hasSpeakerLabels = speakerLabelsEnabled && result.speakerLabels && result.speakerLabels.length > 0;

  if (result.error) {
    return (
      <div className={`border-2 rounded-lg p-4 ${
        result.isBaseModel ? 'border-blue-500' : 'border-green-500'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-900 text-sm">{result.modelName}</h4>
          <span className={`px-2 py-1 text-xs rounded ${
            result.isBaseModel ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
          }`}>
            {result.isBaseModel ? 'Base Model' : 'Custom Model'}
          </span>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <span className="text-red-600 text-lg">❌</span>
            <div className="flex-1">
              <h5 className="text-sm font-semibold text-red-900">Transcription Failed</h5>
              <p className="text-xs text-red-800 mt-1">{result.error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`border-2 rounded-lg p-4 ${
      result.isBaseModel ? 'border-blue-500' : 'border-green-500'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
        <div>
          <h4 className="font-semibold text-gray-900 text-sm">{result.modelName}</h4>
          {result.customizationId && (
            <p className="text-xs text-gray-500 font-mono mt-0.5">ID: {result.customizationId}</p>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 text-xs rounded ${
            result.isBaseModel ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
          }`}>
            {result.isBaseModel ? 'Base Model' : 'Custom Model'}
          </span>
        </div>
      </div>

      {/* Word Count */}
      {result.wordCount && (
        <div className="mb-3 p-2 bg-gray-50 rounded-lg">
          <div className="text-xs">
            <span className="text-gray-600">Words:</span>
            <span className="ml-1 font-semibold text-gray-900">{result.wordCount}</span>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowWordDetails(!showWordDetails)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            {showWordDetails ? 'Hide' : 'Show'} Word Details
          </button>
          {hasEnrichedResults && (
            <button
              onClick={() => setShowEnrichedResults(!showEnrichedResults)}
              className="text-xs text-purple-600 hover:text-purple-800 font-medium"
            >
              {showEnrichedResults ? 'Hide' : 'Show'} Enriched
            </button>
          )}
        </div>
        <span className="text-xs text-gray-500">{mergedResults.length} segments</span>
      </div>

      {/* Enriched Results Panel */}
      {hasEnrichedResults && showEnrichedResults && (
        <div className="mb-3 border-2 border-purple-500 rounded-lg p-3 bg-purple-50">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <h4 className="text-xs font-semibold text-purple-900">🌟 LLM Enriched Transcript</h4>
              {result.enrichedResults.status && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  result.enrichedResults.status === 'success' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {result.enrichedResults.status}
                </span>
              )}
            </div>
          </div>
          
          {/* Show which enrichments were applied */}
          {result.enrichedResults.enrichments && result.enrichedResults.enrichments.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1">
              {result.enrichedResults.enrichments.map((enrichment, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-purple-200 text-purple-900 rounded-full text-xs font-medium">
                  {enrichment}
                </span>
              ))}
            </div>
          )}
          
          {/* Timestamp range */}
          {result.enrichedResults.timestamp && (
            <div className="mb-2 text-xs text-purple-800">
              <span className="font-medium">Time Range:</span>
              <span className="ml-1 font-mono bg-white px-2 py-0.5 rounded">
                {formatTimestamp(result.enrichedResults.timestamp.from)} - {formatTimestamp(result.enrichedResults.timestamp.to)}
              </span>
            </div>
          )}
          
          {/* Enriched transcript text */}
          <div className="bg-white border border-purple-300 rounded-lg p-3">
            <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
              {result.enrichedResults.transcript}
            </p>
          </div>
        </div>
      )}

      {/* Debug: Show if enriched results exist but aren't displaying */}
      {!hasEnrichedResults && result.enrichedResults && (
        <div className="mb-3 border-2 border-yellow-500 rounded-lg p-3 bg-yellow-50">
          <p className="text-xs text-yellow-800">
            ⚠️ Enriched results data exists but transcript is missing
          </p>
          <pre className="text-xs mt-2 bg-white p-2 rounded overflow-auto max-h-32">
            {JSON.stringify(result.enrichedResults, null, 2)}
          </pre>
        </div>
      )}

      {/* Transcripts */}
      <div className="space-y-2 max-h-[500px] overflow-y-auto">
        {mergedResults.map((item, idx) => (
          <div
            key={item.key}
            className={`border-l-4 rounded-r-lg p-2.5 ${
              hasSpeakerLabels 
                ? (item.speaker ? getSpeakerColor(item.speaker) : 'border-gray-400 bg-gray-50') 
                : 'border-blue-500 bg-blue-50'
            }`}
          >
            {/* Timeline header */}
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                {item.startTime !== null && item.endTime !== null && (
                  <span className="font-mono text-xs text-gray-600 bg-white px-2 py-1 rounded shadow-sm">
                    {formatTimestamp(item.startTime)} → {formatTimestamp(item.endTime)}
                  </span>
                )}
                
                {hasSpeakerLabels && (
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                    item.speaker !== null ? 'bg-white shadow-sm' : 'bg-gray-100 text-gray-500'
                  }`}>
                    🎤 {item.speaker !== null ? `Speaker ${item.speaker}` : 'Unknown'}
                  </span>
                )}
                
                {item.startTime !== null && item.endTime !== null && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {(item.endTime - item.startTime).toFixed(1)}s
                  </span>
                )}
              </div>
              
              {item.confidence && (
                <span className={`text-xs font-semibold px-2 py-1 rounded ${getConfidenceColor(item.confidence)}`}>
                  {(item.confidence * 100).toFixed(0)}%
                </span>
              )}
            </div>
            
            {/* Transcript text */}
            <p className="text-sm leading-relaxed text-gray-900 font-medium">
              {item.transcript}
            </p>
            
            {/* Word-level details */}
            {showWordDetails && item.timestamps && item.timestamps.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="flex flex-wrap gap-1">
                  {item.timestamps.map((word, widx) => (
                    <span 
                      key={widx}
                      className="inline-flex items-center bg-white border border-gray-300 rounded px-1.5 py-0.5 text-xs"
                      title={`${formatTimestamp(word[1])} - ${formatTimestamp(word[2])}`}
                    >
                      <span className="font-medium">{word[0]}</span>
                      <span className="ml-1 text-gray-400 font-mono text-[10px]">
                        {formatTimestamp(word[1])}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// FILE: constants/config.js
// ============================================================================
const REGIONS = [
  { code: 'us-east', name: 'US East (Washington DC)', url: 'https://api.us-east.speech-to-text.watson.cloud.ibm.com' },
  { code: 'us-south', name: 'US South (Test)', url: 'https://api.us-south.speech-to-text.test.watson.cloud.ibm.com' }
];

const LANGUAGES = [
  { code: 'en-US', name: 'English' },
  { code: 'es-ES', name: 'Spanish' },
  { code: 'fr-FR', name: 'French' },
  { code: 'de-DE', name: 'German' },
  { code: 'ja-JP', name: 'Japanese' },
  { code: 'ko-KR', name: 'Korean' },
  { code: 'pt-BR', name: 'Portuguese' },
  { code: 'ar-MS', name: 'Arabic' },
  { code: 'zh-CN', name: 'Chinese' },
  { code: 'nl-NL', name: 'Dutch' },
  { code: 'it-IT', name: 'Italian' },
  { code: 'sv-SE', name: 'Swedish' },
];

const MODELS = [
  { value: 'de-DE', label: 'German LSM' },
  { value: 'en-US', label: 'English LSM' },
  { value: 'es-ES', label: 'Spanish LSM' },
  { value: 'fr-FR', label: 'French LSM' },
  { value: 'ja-JP', label: 'Japanese LSM' },
  { value: 'pt-BR', label: 'Portugese LSM' },
  { value: 'ar-MS_Telephony', label: 'Arabic (Modern Standard) - Telephony' },
  { value: 'cs-CZ_Telephony', label: 'Czech - Telephony' },
  { value: 'de-DE_Multimedia', label: 'German - Multimedia' },
  { value: 'de-DE_Telephony', label: 'German - Telephony' },
  { value: 'en-AU_Multimedia', label: 'English (Australian) - Multimedia' },
  { value: 'en-AU_Telephony', label: 'English (Australian) - Telephony' },
  { value: 'en-GB_Multimedia', label: 'English (UK) - Multimedia' },
  { value: 'en-GB_Telephony', label: 'English (UK) - Telephony' },
  { value: 'en-IN_Telephony', label: 'English (Indian) - Telephony' },
  { value: 'en-US_Multimedia', label: 'English (US) - Multimedia' },
  { value: 'en-US_Telephony', label: 'English (US) - Telephony' },
  { value: 'en-WW_Medical_Telephony', label: 'English (Medical) - Telephony' },
  { value: 'es-AR_Telephony', label: 'Spanish (Argentinian) - Telephony' },
  { value: 'es-CL_Telephony', label: 'Spanish (Chilean) - Telephony' },
  { value: 'es-CO_Telephony', label: 'Spanish (Colombian) - Telephony' },
  { value: 'es-ES_Multimedia', label: 'Spanish (Spain) - Multimedia' },
  { value: 'es-ES_Telephony', label: 'Spanish (Spain) - Telephony' },
  { value: 'es-LA_Telephony', label: 'Spanish (Latin American) - Telephony' },
  { value: 'es-MX_Telephony', label: 'Spanish (Mexican) - Telephony' },
  { value: 'es-PE_Telephony', label: 'Spanish (Peruvian) - Telephony' },
  { value: 'fr-CA_Multimedia', label: 'French (Canadian) - Multimedia' },
  { value: 'fr-CA_Telephony', label: 'French (Canadian) - Telephony' },
  { value: 'fr-FR_Multimedia', label: 'French - Multimedia' },
  { value: 'fr-FR_Telephony', label: 'French - Telephony' },
  { value: 'hi-IN_Telephony', label: 'Hindi (Indian) - Telephony' },
  { value: 'it-IT_Multimedia', label: 'Italian - Multimedia' },
  { value: 'it-IT_Telephony', label: 'Italian - Telephony' },
  { value: 'ja-JP_Multimedia', label: 'Japanese - Multimedia' },
  { value: 'ja-JP_Telephony', label: 'Japanese - Telephony' },
  { value: 'ko-KR_Multimedia', label: 'Korean - Multimedia' },
  { value: 'ko-KR_Telephony', label: 'Korean - Telephony' },
  { value: 'nl-BE_Telephony', label: 'Dutch (Belgian) - Telephony' },
  { value: 'nl-NL_Multimedia', label: 'Dutch - Multimedia' },
  { value: 'nl-NL_Telephony', label: 'Dutch - Telephony' },
  { value: 'pt-BR_Multimedia', label: 'Portuguese (Brazilian) - Multimedia' },
  { value: 'pt-BR_Telephony', label: 'Portuguese (Brazilian) - Telephony' },
  { value: 'sv-SE_Telephony', label: 'Swedish - Telephony' },
  { value: 'zh-CN_Telephony', label: 'Chinese (Mandarin) - Telephony' },
];

const SMART_FORMATTING_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'v1', label: 'Version 1' },
  { value: 'v2', label: 'Version 2' }
];

const SAMPLE_RATE = {
  NARROWBAND: 8000,
  BROADBAND: 16000
};

const TABS = {
  AUDIO: 'audio',
  LIVE: 'live',
  STORIES: 'stories',
  SETTINGS: 'settings'
};

// Demo limits (in seconds)
const DEMO_LIMITS = {
  MAX_UPLOAD_SIZE: 5 * 1024 * 1024, // 5MB
  MAX_RECORDING_TIME: 120, // 2 minutes
  MAX_STREAMING_TIME: 120 // 2 minutes
};

// Sample audio will be loaded from samples.json
const SAMPLE_AUDIO = [];

// ============================================================================
// FILE: services/watsonSTT.js
// ============================================================================
class WatsonSTTService {
  constructor(apiKey, serviceUrl) {
    this.apiKey = apiKey;
    this.serviceUrl = serviceUrl;
    this.ws = null;
    this.useProxy = this.shouldUseProxy();
  }

  shouldUseProxy() {
    // Always use proxy - backend handles credentials
    return true;
  }

  getProxyUrl() {
    // In dev mode (port 3000), proxy is at localhost:8080
    // In production (port 8080), proxy is at same origin
    return window.location.port === '3000' ? 'http://localhost:8080' : '';
  }

  async identifyLanguage(audioFile, contentType = 'audio/wav') {
    // Extract first 5 seconds of audio
    let audioToSend = audioFile;
    
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await audioFile.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Only process if audio is longer than 5 seconds
      if (audioBuffer.duration > 5) {
        const sampleRate = audioBuffer.sampleRate;
        const numChannels = audioBuffer.numberOfChannels;
        const numSamples = Math.floor(5 * sampleRate); // 5 seconds
        
        // Create a new buffer with only first 5 seconds
        const trimmedBuffer = audioContext.createBuffer(
          numChannels,
          numSamples,
          sampleRate
        );
        
        // Copy first 5 seconds of audio data
        for (let channel = 0; channel < numChannels; channel++) {
          const sourceData = audioBuffer.getChannelData(channel);
          const targetData = trimmedBuffer.getChannelData(channel);
          for (let i = 0; i < numSamples; i++) {
            targetData[i] = sourceData[i];
          }
        }
        
        // Convert to WAV blob
        const wavBlob = this.audioBufferToWav(trimmedBuffer);
        audioToSend = wavBlob;
        contentType = 'audio/wav';
        
        console.log(`Sending first 5 seconds of audio for language identification (original: ${audioBuffer.duration.toFixed(1)}s)`);
      } else {
        console.log(`Sending full audio for language identification (${audioBuffer.duration.toFixed(1)}s)`);
      }
    } catch (err) {
      console.warn('Could not trim audio, sending full file:', err.message);
    }
    
    // Always use proxy for language identification
    const proxyUrl = this.getProxyUrl();
    
    try {
      const response = await fetch(`${proxyUrl}/api/watson/detect-language`, {
        method: 'POST',
        headers: {
          'Content-Type': contentType,
          'x-service-url': this.serviceUrl,
          'x-api-key': this.apiKey || 'from-env'
        },
        body: audioToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          code: errorData.code || response.status,
          code_description: errorData.code_description || response.statusText,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`
        };
      }

      return await response.json();
    } catch (error) {
      // If error is already structured, throw it as-is
      if (error.code && error.error) {
        throw error;
      }
      // Otherwise wrap it
      throw {
        code: 500,
        code_description: 'Language Identification Failed',
        error: error.message || 'Unknown error occurred'
      };
    }
  }

  // Helper function to convert AudioBuffer to WAV
  audioBufferToWav(audioBuffer) {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numberOfChannels * bytesPerSample;
    
    const data = [];
    for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
      data.push(audioBuffer.getChannelData(i));
    }
    
    const interleaved = this.interleave(data);
    const dataLength = interleaved.length * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataLength);
    const view = new DataView(buffer);
    
    // Write WAV header
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    this.writeString(view, 8, 'WAVE');
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    this.writeString(view, 36, 'data');
    view.setUint32(40, dataLength, true);
    
    // Write audio data
    this.floatTo16BitPCM(view, 44, interleaved);
    
    return new Blob([buffer], { type: 'audio/wav' });
  }
  
  interleave(channelData) {
    const length = channelData[0].length;
    const result = new Float32Array(length * channelData.length);
    let offset = 0;
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < channelData.length; channel++) {
        result[offset++] = channelData[channel][i];
      }
    }
    return result;
  }
  
  writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }
  
  floatTo16BitPCM(view, offset, input) {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
  }

  async transcribeFile(audioFile, model = 'en-US_BroadbandModel', params = {}) {
    // Always use proxy if available (this.useProxy is true when no API key or in dev mode)
    if (this.useProxy) {
      const proxyUrl = this.getProxyUrl();
      
      console.log('=== PROXY REQUEST ===');
      console.log('Routing through backend proxy');
      console.log('Model:', model);
      console.log('=====================');
      
      try {
        const response = await fetch(`${proxyUrl}/api/watson/transcribe`, {
          method: 'POST',
          headers: {
            'Content-Type': params.contentType || audioFile.type || 'audio/wav',
            'x-service-url': this.serviceUrl,
            'x-model': model,
            'x-api-key': this.apiKey || 'from-env',
            'x-params': JSON.stringify(params)
          },
          body: audioFile
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorData = {
            code: response.status,
            code_description: response.statusText,
            error: `HTTP ${response.status}: ${response.statusText}`
          };
          
          try {
            const errorJson = JSON.parse(errorText);
            if (errorJson.error) errorData.error = errorJson.error;
            if (errorJson.code) errorData.code = errorJson.code;
            if (errorJson.code_description) errorData.code_description = errorJson.code_description;
          } catch (parseErr) {
            // Use default error data
          }
          
          throw errorData;
        }

        return await response.json();
      } catch (error) {
        if (error.code && error.error) {
          throw error;
        }
        throw new Error(`Transcription failed: ${error.message}`);
      }
    }
    
    // Direct API call (production mode with credentials)
    const queryParams = new URLSearchParams({
      model: model,
      timestamps: params.timestamps !== undefined ? params.timestamps : true,
      speaker_labels: params.speaker_labels || false,
      audio_metrics: params.audio_metrics || false,
      low_latency: params.low_latency || false
    });

    if (params.speech_detector_sensitivity !== undefined && params.speech_detector_sensitivity !== '') {
      queryParams.append('speech_detector_sensitivity', params.speech_detector_sensitivity);
    }
    if (params.background_audio_suppression !== undefined && params.background_audio_suppression !== '') {
      queryParams.append('background_audio_suppression', params.background_audio_suppression);
    }
    if (params.end_of_phrase_silence_time !== undefined && params.end_of_phrase_silence_time !== '') {
      queryParams.append('end_of_phrase_silence_time', params.end_of_phrase_silence_time);
    }
    if (params.sad_module !== undefined && params.sad_module !== '') {
      const sadModuleInt = parseInt(params.sad_module);
      if (sadModuleInt === 1 || sadModuleInt === 2) {
        queryParams.append('sad_module', sadModuleInt);
      }
    }
    
    // Smart formatting
    if (params.smart_formatting && params.smart_formatting !== 'none') {
      queryParams.append('smart_formatting', true);
      if (params.smart_formatting === 'v2') {
        queryParams.append('smart_formatting_version', 2);
      } else if (params.smart_formatting === 'v1') {
        queryParams.append('smart_formatting_version', 1);
      }
    }

    // Speech enrichment - send as array if not 'none'
    if (params.enrichments && Array.isArray(params.enrichments) && 
        params.enrichments.length > 0 && 
        !params.enrichments.includes('none')) {
      queryParams.append('enrichments', params.enrichments.join(','));
    }

    const url = `${this.serviceUrl}/v1/recognize?${queryParams.toString()}`;
    
    // Detect content type from file
    let contentType = params.contentType || audioFile.type || 'audio/wav';
    
    console.log('=== REST API REQUEST ===');
    console.log('Host:', new URL(url).hostname);
    console.log('Method: POST');
    console.log('Content-Type:', contentType);
    console.log('Model:', model);
    console.log('Parameters:', {
      timestamps: params.timestamps,
      speaker_labels: params.speaker_labels,
      audio_metrics: params.audio_metrics,
      low_latency: params.low_latency,
      speech_detector_sensitivity: params.speech_detector_sensitivity,
      background_audio_suppression: params.background_audio_suppression,
      end_of_phrase_silence_time: params.end_of_phrase_silence_time,
      sad_module: params.sad_module,
      smart_formatting: params.smart_formatting,
      enrichments: params.enrichments
    });
    console.log('========================');
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': 'Basic ' + btoa('apikey:' + this.apiKey),
          'Content-Type': contentType
        },
        body: audioFile
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData = {
          code: response.status,
          code_description: response.statusText,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
        
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error) {
            errorData.error = errorJson.error;
          }
          if (errorJson.code) {
            errorData.code = errorJson.code;
          }
          if (errorJson.code_description) {
            errorData.code_description = errorJson.code_description;
          }
        } catch (parseErr) {
          // If parsing fails, use the default error data
        }
        
        throw errorData;
      }

      return await response.json();
    } catch (error) {
      if (error.code && error.error) {
        throw error;
      }
      throw new Error(`Transcription failed: ${error.message}`);
    }
  }

  async connectWebSocket(model, params, onMessage, onError) {
    try {
      // Get token from backend instead of using API key
      const proxyUrl = this.getProxyUrl();
      
      console.log('🔄 Requesting authentication token from backend...');
      
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 35000); // 35 second timeout (5s buffer over server)
      
      const tokenResponse = await fetch(`${proxyUrl}/api/watson/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ serviceUrl: this.serviceUrl }),
        signal: controller.signal
      });
      
      clearTimeout(timeout);
      
      if (!tokenResponse.ok) {
        const error = await tokenResponse.json();
        throw new Error(error.error || 'Failed to get authentication token');
      }
      
      const { access_token } = await tokenResponse.json();
      console.log('✓ Token received successfully');
      
      // Extract hostname from service URL
      const urlObj = new URL(this.serviceUrl);
      const hostname = urlObj.hostname;
      
      // Build WebSocket URL with token authentication
      // Watson STT WebSocket API accepts access_token in URL (browser WebSocket doesn't support custom headers)
      const wsUrl = `wss://${hostname}/v1/recognize?access_token=${encodeURIComponent(access_token)}&model=${model}`;
      
      console.log('🔌 Connecting to WebSocket:', hostname);
      
      this.ws = new WebSocket(wsUrl);

      // Return a promise that resolves when connection is established
      return new Promise((resolve, reject) => {
        // Add connection timeout
        const connectTimeout = setTimeout(() => {
          if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
            this.ws.close();
            reject(new Error('WebSocket connection timed out after 15 seconds'));
          }
        }, 15000);

        this.ws.onopen = () => {
          clearTimeout(connectTimeout);
          console.log('✓ WebSocket connected');
          console.log('Sample rate for L16:', params.sampleRate);
          
          const contentType = `audio/l16;rate=${params.sampleRate || 16000}`;
          console.log('Using content-type:', contentType);
          
          const message = {
            action: 'start',
            'content-type': contentType,
            interim_results: params.interim_results !== undefined ? params.interim_results : true,
            timestamps: params.timestamps !== undefined ? params.timestamps : true,
            word_confidence: true,
            speaker_labels: params.speaker_labels || false,
            audio_metrics: params.audio_metrics || false,
            processing_metrics: params.processing_metrics || false,
            processing_metrics_interval: params.processing_metrics ? 0.5 : undefined,
            low_latency: params.low_latency || false
          };

          if (params.speech_detector_sensitivity !== undefined && params.speech_detector_sensitivity !== '') {
            message.speech_detector_sensitivity = params.speech_detector_sensitivity;
          }
          if (params.background_audio_suppression !== undefined && params.background_audio_suppression !== '') {
            message.background_audio_suppression = params.background_audio_suppression;
          }
          if (params.end_of_phrase_silence_time !== undefined && params.end_of_phrase_silence_time !== '') {
            message.end_of_phrase_silence_time = params.end_of_phrase_silence_time;
          }
          if (params.sad_module !== undefined && params.sad_module !== '') {
            const sadModuleInt = parseInt(params.sad_module);
            if (sadModuleInt === 1 || sadModuleInt === 2) {
              message.sad_module = sadModuleInt;
            }
          }
          
          // Smart formatting
          if (params.smart_formatting && params.smart_formatting !== 'none') {
            message.smart_formatting = true;
            if (params.smart_formatting === 'v2') {
              message.smart_formatting_version = 2;
            } else if (params.smart_formatting === 'v1') {
              message.smart_formatting_version = 1;
            }
          }

          // Speech enrichment - send as array if not 'none'
          if (params.enrichments && Array.isArray(params.enrichments) && 
              params.enrichments.length > 0 && 
              !params.enrichments.includes('none')) {
            message.enrichments = params.enrichments;
          }

          console.log('=== WEBSOCKET START MESSAGE ===');
          console.log('Host:', hostname);
          console.log('Model:', model);
          console.log('Parameters:', JSON.stringify(message, null, 2));
          console.log('==============================');
          this.ws.send(JSON.stringify(message));
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Check for error in the message
            if (data.error) {
              if (onError) {
                onError({
                  message: data.error,
                  code: data.code,
                  code_description: data.code_description
                });
              }
              return;
            }
            
            if (onMessage) onMessage(data);
          } catch (err) {
            if (onError) {
              onError({
                message: 'Failed to parse WebSocket message: ' + err.message
              });
            }
          }
        };

        this.ws.onerror = (error) => {
          clearTimeout(connectTimeout);
          console.error('WebSocket error event:', error);
          if (onError) {
            onError({
              message: 'WebSocket connection error. Please check your network connection.',
              type: 'connection_error'
            });
          }
          reject(new Error('WebSocket connection error'));
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectTimeout);
          console.log('WebSocket closed', event);
          
          // Check if close was abnormal
          if (!event.wasClean && onError) {
            let errorMessage = 'WebSocket connection closed unexpectedly';
            
            if (event.code === 1006) {
              errorMessage = 'WebSocket connection failed. Please verify your network connection.';
            } else if (event.code === 1008) {
              errorMessage = 'WebSocket connection rejected. Please check your API credentials.';
            } else if (event.code !== 1000) {
              errorMessage = `WebSocket closed with code ${event.code}: ${event.reason || 'Unknown reason'}`;
            }
            
            onError({
              message: errorMessage,
              code: event.code,
              type: 'close_error'
            });
          }
        };
      });
    } catch (error) {
      console.error('Connection setup error:', error);
      
      let errorMessage = error.message;
      if (error.name === 'AbortError') {
        errorMessage = 'Token request timed out after 35 seconds. This may indicate a network issue in your Docker container. Check server logs for details.';
      }
      
      if (onError) {
        onError({
          message: errorMessage || 'Failed to establish WebSocket connection',
          type: 'connection_error'
        });
      }
      throw error;
    }
  }

  sendAudioData(audioData) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(audioData);
    }
  }

  closeWebSocket() {
    if (this.ws) {
      const stopMessage = { action: 'stop' };
      this.ws.send(JSON.stringify(stopMessage));
      
      // Wait 1 second for final response (including audio_metrics) before closing
      setTimeout(() => {
        if (this.ws) {
          this.ws.close();
          this.ws = null;
        }
      }, 1000);
    }
  }

  async connectWebSocketLID(params, onMessage, onError) {
    try {
      const proxyUrl = this.getProxyUrl();

      console.log('🔄 Requesting authentication token for LID...');

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 35000);

      const tokenResponse = await fetch(`${proxyUrl}/api/watson/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serviceUrl: this.serviceUrl }),
        signal: controller.signal
      });

      clearTimeout(timeout);

      if (!tokenResponse.ok) {
        const error = await tokenResponse.json();
        throw new Error(error.error || 'Failed to get authentication token');
      }

      const { access_token } = await tokenResponse.json();
      console.log('✓ LID token received');

      const urlObj = new URL(this.serviceUrl);
      const hostname = urlObj.hostname;

      const wsUrl = `wss://${hostname}/v1/detect_language?access_token=${encodeURIComponent(access_token)}`;

      console.log('🔌 Connecting to LID WebSocket:', hostname);

      this.wsLID = new WebSocket(wsUrl);

      return new Promise((resolve, reject) => {
        const connectTimeout = setTimeout(() => {
          if (this.wsLID && this.wsLID.readyState !== WebSocket.OPEN) {
            this.wsLID.close();
            reject(new Error('LID WebSocket connection timed out after 15 seconds'));
          }
        }, 15000);

        this.wsLID.onopen = () => {
          clearTimeout(connectTimeout);
          console.log('✓ LID WebSocket connected');

          const contentType = `audio/l16;rate=${params.sampleRate || 16000}`;
          const message = {
            action: 'start',
            'content-type': contentType
          };

          console.log('=== LID WEBSOCKET START MESSAGE ===');
          console.log('Host:', hostname);
          console.log('Parameters:', JSON.stringify(message, null, 2));
          console.log('===================================');
          this.wsLID.send(JSON.stringify(message));
          resolve();
        };

        this.wsLID.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.error) {
              if (onError) onError({ message: data.error, code: data.code, code_description: data.code_description });
              return;
            }
            if (onMessage) onMessage(data);
          } catch (err) {
            if (onError) onError({ message: 'Failed to parse LID WebSocket message: ' + err.message });
          }
        };

        this.wsLID.onerror = (error) => {
          clearTimeout(connectTimeout);
          console.error('LID WebSocket error:', error);
          if (onError) onError({ message: 'LID WebSocket connection error. Please check your network connection.', type: 'connection_error' });
          reject(new Error('LID WebSocket connection error'));
        };

        this.wsLID.onclose = (event) => {
          clearTimeout(connectTimeout);
          console.log('LID WebSocket closed', event);
          if (!event.wasClean && onError) {
            let errorMessage = 'LID WebSocket connection closed unexpectedly';
            if (event.code === 1006) errorMessage = 'LID WebSocket connection failed. Please verify your network connection.';
            else if (event.code === 1008) errorMessage = 'LID WebSocket rejected. Please check your API credentials.';
            else if (event.code !== 1000) errorMessage = `LID WebSocket closed with code ${event.code}: ${event.reason || 'Unknown reason'}`;
            onError({ message: errorMessage, code: event.code, type: 'close_error' });
          }
        };
      });
    } catch (error) {
      console.error('LID connection setup error:', error);
      let errorMessage = error.message;
      if (error.name === 'AbortError') errorMessage = 'LID token request timed out after 35 seconds.';
      if (onError) onError({ message: errorMessage || 'Failed to establish LID WebSocket connection', type: 'connection_error' });
      throw error;
    }
  }

  sendLIDAudioData(audioData) {
    if (this.wsLID && this.wsLID.readyState === WebSocket.OPEN) {
      this.wsLID.send(audioData);
    }
  }

  closeWebSocketLID() {
    if (this.wsLID) {
      try {
        const stopMessage = { action: 'stop' };
        this.wsLID.send(JSON.stringify(stopMessage));
      } catch (e) { /* ignore */ }
      this.wsLID.close();
      this.wsLID = null;
    }
  }
}

// ============================================================================
// FILE: components/Header.js
// ============================================================================
function Header({ onShowHelp }) {
  return (
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-5 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Mic className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">IBM Watson Speech to Text - Demo</h1>
              <p className="text-blue-100 text-xs mt-0.5">Interactive demo showcasing STT capabilities</p>
            </div>
          </div>
          <button
            onClick={onShowHelp}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors rounded-lg px-3 py-2 flex items-center space-x-2 text-sm"
            title="Keyboard shortcuts"
          >
            <span>⌨️</span>
            <span className="hidden sm:inline">Shortcuts</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FILE: components/ProductInfo.js
// ============================================================================
function ProductInfo() {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-md p-6 border-2 border-blue-200">
      <div className="flex items-start space-x-4">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Mic className="w-8 h-8 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900 mb-2">IBM Watson Speech to Text</h2>
          <p className="text-sm text-gray-700 leading-relaxed mb-4">
            Convert speech into text using AI-powered speech recognition and transcription. Our advanced machine learning models deliver fast and accurate speech transcription in multiple languages for customer self-service, agent assistance, and speech analytics.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="flex items-start space-x-2">
              <span className="text-blue-600 mt-0.5">✓</span>
              <div>
                <h3 className="text-xs font-semibold text-gray-900">Best-in-Class AI</h3>
                <p className="text-xs text-gray-600">Advanced models that truly understand your customers</p>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-blue-600 mt-0.5">✓</span>
              <div>
                <h3 className="text-xs font-semibold text-gray-900">Customizable</h3>
                <p className="text-xs text-gray-600">Train models on your domain language and audio</p>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-blue-600 mt-0.5">✓</span>
              <div>
                <h3 className="text-xs font-semibold text-gray-900">Enterprise Security</h3>
                <p className="text-xs text-gray-600">IBM's world-class data governance and protection</p>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-blue-600 mt-0.5">✓</span>
              <div>
                <h3 className="text-xs font-semibold text-gray-900">Deploy Anywhere</h3>
                <p className="text-xs text-gray-600">Public, private, hybrid, multicloud, or on-premises</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <a
              href="https://www.ibm.com/products/speech-to-text"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
            >
              <span>Product Page</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a
              href="https://cloud.ibm.com/apidocs/speech-to-text"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-1.5 px-4 py-2 bg-white hover:bg-gray-50 text-blue-600 text-sm font-medium rounded-lg transition-colors border-2 border-blue-600 shadow-sm"
            >
              <span>API Documentation</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a
              href="https://cloud.ibm.com/catalog/services/speech-to-text"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
            >
              <span>Start Free Trial</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FILE: components/TabNavigation.js
// ============================================================================
function TabNavigation({ activeTab, onTabChange, audioCount, storiesCount }) {
  const tabs = [
    { id: TABS.SETTINGS, label: 'Product Info & Settings', icon: Settings },
    { id: TABS.AUDIO, label: 'Audio Files', icon: FileAudio, badge: audioCount },
    { id: TABS.LIVE, label: 'Live Recording', icon: Mic },
    { id: TABS.STORIES, label: 'Use Case Stories', icon: FileAudio, badge: storiesCount }
  ];

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex space-x-1">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`flex items-center space-x-2 px-5 py-3 border-b-2 font-medium transition-colors relative text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
                {tab.badge > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FILE: components/InfoLink.js
// ============================================================================
function InfoLink({ href, tooltip }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 transition-colors ml-1.5 group relative"
      title={tooltip}
    >
      <Info className="w-3 h-3" />
      <ExternalLink className="w-2 h-2 absolute -top-0.5 -right-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
    </a>
  );
}

// ============================================================================
// FILE: components/SettingsTab.js
// ============================================================================
function SettingsTab({ config, onConfigChange, showProductInfo }) {
  const [credentials, setCredentials] = useState({});
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(true);
  const [credentialsError, setCredentialsError] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);

  // Helper to check if model is LSM
  const isLSMModel = (modelValue) => {
    // LSM models are: de-DE, en-US, es-ES, fr-FR, ja-JP, pt-BR (without suffix)
    const lsmModels = ['de-DE', 'en-US', 'es-ES', 'fr-FR', 'ja-JP', 'pt-BR'];
    return lsmModels.includes(modelValue);
  };

  // Handle model change with automatic parameter adjustment
  const handleModelChange = (newModel) => {
    const isLSM = isLSMModel(newModel);
    
    // Update config with model and appropriate defaults
    onConfigChange({
      ...config,
      model: newModel,
      speech_detector_sensitivity: isLSM ? 0.7 : 0.5,
      sad_module: isLSM ? '2' : '1'
    });
  };

  useEffect(() => {
    const loadCredentials = async () => {
      setIsLoadingCredentials(true);
      
      try {
        // Check if backend proxy is available
        const backendUrl = window.location.port === '3000' 
          ? 'http://localhost:8080' 
          : '';
        
        const response = await fetch(`${backendUrl}/api/watson/health`);
        
        if (response.ok) {
          console.log('✓ Backend proxy available - all API calls will use server-side credentials');
          // Backend is running, all transcription calls will be proxied
          // Set a flag to indicate proxy mode (no credentials in browser)
          setCredentials({ proxyMode: true });
        } else {
          throw new Error('Backend server not responding');
        }
      } catch (err) {
        console.warn('⚠️ Backend proxy not available');
        console.log('Manual API key entry required');
        setCredentialsError('Backend proxy not available');
      } finally {
        setIsLoadingCredentials(false);
      }
    };

    loadCredentials();
  }, [config.serviceUrl]);

  // Always show API key input if no API key is present or credentials are missing
  useEffect(() => {
    const hasApiKey = config.apiKey && config.apiKey.trim() !== '';
    const hasProxyMode = credentials.proxyMode === true;
    // Show input if: no API key AND not in proxy mode
    setShowApiKeyInput(!hasApiKey && !hasProxyMode);
  }, [config.apiKey, credentials]);

  const handleRegionChange = (newServiceUrl) => {
    const region = REGIONS.find(r => r.url === newServiceUrl);
    const regionCode = region?.code || 'us-south';
    
    // Load API key for selected region from credentials
    const apiKey = credentials[regionCode]?.apiKey || '';
    
    onConfigChange({ 
      ...config, 
      serviceUrl: newServiceUrl, 
      region: regionCode,
      apiKey: apiKey
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-5">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">API Configuration</h2>
      
      {isLoadingCredentials && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm text-blue-800">Loading credentials...</span>
        </div>
      )}

      {credentialsError && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm text-yellow-800">⚠️ Backend proxy not available. Please enter your API key below to use the demo.</p>
        </div>
      )}
      
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
              Region
              <InfoLink 
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-service-features#features-regions"
                tooltip="Learn about service regions"
              />
            </label>
            <select
              value={config.serviceUrl}
              onChange={(e) => handleRegionChange(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {REGIONS.map(region => (
                <option key={region.code} value={region.url}>
                  {region.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-0.5">
              Select your service region
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
              Model
              <InfoLink 
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-models"
                tooltip="Learn about available speech recognition models"
              />
            </label>
            <select
              value={config.model}
              onChange={(e) => handleModelChange(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {MODELS.map(model => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-0.5">
              {isLSMModel(config.model) ? '✓ LSM model - optimized defaults applied' : 'Non-LSM model'}
            </p>
          </div>
        </div>

        {showApiKeyInput && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
              API Key (Optional - only needed if backend proxy is not available)
              <InfoLink 
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-gettingStarted#getting-started-before-you-begin"
                tooltip="Learn how to get your API key"
              />
            </label>
            <input
              type="password"
              value={config.apiKey}
              onChange={(e) => onConfigChange({ ...config, apiKey: e.target.value })}
              placeholder="Enter your Watson STT API key (optional)"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
            />
            <p className="text-xs text-gray-500 mt-1">
              Only required if backend proxy is unavailable. When using the proxy, credentials are handled server-side.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
              Speech Detector Sensitivity
              <InfoLink 
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-detection#detection-parameters-sensitivity"
                tooltip="Learn about speech detection sensitivity"
              />
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={config.speech_detector_sensitivity}
              onChange={(e) => onConfigChange({ ...config, speech_detector_sensitivity: parseFloat(e.target.value) })}
              placeholder="0.0 - 1.0"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-0.5">
              Range: 0.0 to 1.0 (Recommended: {isLSMModel(config.model) ? '0.7 for LSM' : '0.5 for non-LSM'})
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
              Background Audio Suppression
              <InfoLink 
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-detection#detection-parameters-suppression"
                tooltip="Learn about background audio suppression"
              />
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={config.background_audio_suppression}
              onChange={(e) => onConfigChange({ ...config, background_audio_suppression: parseFloat(e.target.value) })}
              placeholder="0.0 - 1.0"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-0.5">Range: 0.0 to 1.0</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
              End of Phrase Silence Time
              <InfoLink 
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-detection#detection-parameters-silence-time"
                tooltip="Learn about end of phrase silence time"
              />
            </label>
            <input
              type="number"
              step="0.1"
              min="0.0"
              max="2.0"
              value={config.end_of_phrase_silence_time}
              onChange={(e) => onConfigChange({ ...config, end_of_phrase_silence_time: parseFloat(e.target.value) })}
              placeholder="0.0 - 2.0"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-0.5">Range: 0.0 to 2.0 (default: 0.8)</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              SAD Module
            </label>
            <select
              value={config.sad_module}
              onChange={(e) => onConfigChange({ ...config, sad_module: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Default</option>
              <option value="1">1 - Legacy</option>
              <option value="2">2 - Next-gen</option>
            </select>
            <p className="text-xs text-gray-500 mt-0.5">
              Speech Activity Detection (Recommended: {isLSMModel(config.model) ? 'Module 2 for LSM' : 'Module 1 for non-LSM'})
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
              Smart Formatting
              <InfoLink 
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-formatting#smart-formatting"
                tooltip="Learn about smart formatting options"
              />
            </label>
            <select
              value={config.smart_formatting}
              onChange={(e) => onConfigChange({ ...config, smart_formatting: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {SMART_FORMATTING_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-0.5">Format dates, numbers, emails</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
              LLM Speech Enrichment
              <InfoLink 
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-speech-transcript-enrichment"
                tooltip="Learn about LLM-powered speech transcript enrichment"
              />
            </label>
            <div className="space-y-1.5">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.enrichments.includes('punctuation')}
                  onChange={(e) => {
                    const newEnrichments = e.target.checked 
                      ? [...config.enrichments.filter(e => e !== 'none'), 'punctuation']
                      : config.enrichments.filter(e => e !== 'punctuation');
                    onConfigChange({ ...config, enrichments: newEnrichments.length > 0 ? newEnrichments : ['none'] });
                  }}
                  className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-xs text-gray-700">Punctuation</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.enrichments.includes('smart_formatting')}
                  onChange={(e) => {
                    const newEnrichments = e.target.checked 
                      ? [...config.enrichments.filter(e => e !== 'none'), 'smart_formatting']
                      : config.enrichments.filter(e => e !== 'smart_formatting');
                    onConfigChange({ ...config, enrichments: newEnrichments.length > 0 ? newEnrichments : ['none'] });
                  }}
                  className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-xs text-gray-700">Smart Formatting</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">LLM-powered enhancements</p>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Options
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.timestamps}
                onChange={(e) => onConfigChange({ ...config, timestamps: e.target.checked })}
                className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-xs text-gray-700">Timestamps</span>
              <InfoLink 
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-summary#summary-timestamps"
                tooltip="Learn about timestamps"
              />
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.speaker_labels}
                onChange={(e) => onConfigChange({ ...config, speaker_labels: e.target.checked })}
                className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-xs text-gray-700">Speaker Labels</span>
              <InfoLink 
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-speaker-labels"
                tooltip="Learn about speaker labels"
              />
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.interim_results}
                onChange={(e) => onConfigChange({ ...config, interim_results: e.target.checked })}
                className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-xs text-gray-700">Interim Results</span>
              <InfoLink 
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-interim#interim-results"
                tooltip="Learn about interim results"
              />
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.audio_metrics}
                onChange={(e) => onConfigChange({ ...config, audio_metrics: e.target.checked })}
                className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-xs text-gray-700">Audio Metrics</span>
              <InfoLink 
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-summary#summary-audio-metrics"
                tooltip="Learn about audio metrics"
              />
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.processing_metrics}
                onChange={(e) => onConfigChange({ ...config, processing_metrics: e.target.checked })}
                className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-xs text-gray-700">Processing Metrics</span>
              <InfoLink
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-metrics#processing-metrics"
                tooltip="Learn about processing metrics"
              />
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.low_latency}
                onChange={(e) => onConfigChange({ ...config, low_latency: e.target.checked })}
                className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-xs text-gray-700">Low Latency</span>
              <InfoLink
                href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-interim#low-latency"
                tooltip="Learn about low latency mode"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FILE: components/AudioTab.js
// ============================================================================
function AudioTab({ audioFiles, selectedFile, onSelectFile, onDeleteFile, onTranscribe, isTranscribing, onAddFile, config, isLoadingSamples }) {
  const [language, setLanguage] = useState('en-US');
  const [sampleRate, setSampleRate] = useState(SAMPLE_RATE.BROADBAND);
  const [error, setError] = useState('');
  const [lidResult, setLidResult] = useState(null);
  const [isIdentifying, setIsIdentifying] = useState(false);
  const fileInputRef = useRef(null);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatSize = (bytes) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const getSampleRateLabel = (rate) => {
    return rate === 8000 ? '8kHz' : '16kHz';
  };

  const getFormatLabel = (contentType) => {
    if (!contentType) return '';
    if (contentType.includes('wav')) return 'WAV';
    if (contentType.includes('mp3')) return 'MP3';
    if (contentType.includes('flac')) return 'FLAC';
    if (contentType.includes('ogg')) return 'OGG';
    if (contentType.includes('webm')) return 'WEBM';
    if (contentType.includes('opus')) return 'OPUS';
    return '';
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setError('');
    setLidResult(null);

    // Check file size limit
    if (selectedFile.size > DEMO_LIMITS.MAX_UPLOAD_SIZE) {
      setError(`File size exceeds the demo limit of ${(DEMO_LIMITS.MAX_UPLOAD_SIZE / 1024 / 1024).toFixed(0)}MB. Please upload a smaller file.`);
      return;
    }

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await selectedFile.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      const duration = audioBuffer.duration;

      // Check duration limit
      if (duration > DEMO_LIMITS.MAX_RECORDING_TIME) {
        setError(`Audio duration exceeds the demo limit of ${Math.floor(DEMO_LIMITS.MAX_RECORDING_TIME / 60)} minutes. Please upload a shorter file.`);
        return;
      }
      
      let contentType = selectedFile.type;
      const fileName = selectedFile.name.toLowerCase();
      
      if (fileName.endsWith('.wav')) {
        contentType = 'audio/wav';
      } else if (fileName.endsWith('.mp3')) {
        contentType = 'audio/mp3';
      } else if (fileName.endsWith('.flac')) {
        contentType = 'audio/flac';
      } else if (fileName.endsWith('.ogg')) {
        contentType = 'audio/ogg';
      } else if (fileName.endsWith('.webm')) {
        contentType = 'audio/webm';
      } else if (fileName.endsWith('.opus')) {
        contentType = 'audio/ogg;codecs=opus';
      }

      const audioFile = {
        id: `upload-${Date.now()}`,
        name: selectedFile.name,
        language: language,
        sampleRate: sampleRate,
        duration: duration,
        size: selectedFile.size,
        blob: selectedFile,
        url: URL.createObjectURL(selectedFile),
        contentType: contentType,
        isPreloaded: false
      };

      onAddFile(audioFile);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError('Failed to process audio file: ' + err.message);
    }
  };

  const handleIdentifyLanguage = async () => {
    if (!selectedFile || !config.serviceUrl) {
      setError('Please select a file first');
      return;
    }

    setIsIdentifying(true);
    setError('');
    setLidResult(null);

    try {
      const service = new WatsonSTTService(config.apiKey, config.serviceUrl);
      
      let audioBlob = selectedFile.blob;
      let contentType = selectedFile.contentType || 'audio/wav';
      
      if (selectedFile.isPreloaded && selectedFile.url) {
        const response = await fetch(selectedFile.url);
        audioBlob = await response.blob();
        contentType = audioBlob.type || 'audio/wav';
      }

      const result = await service.identifyLanguage(audioBlob, contentType);
      setLidResult(result);
    } catch (err) {
      // Handle structured errors from backend
      if (err.code && err.error) {
        setError(`Language identification failed (${err.code}): ${err.error}`);
      } else {
        setError('Language identification failed: ' + err.message);
      }
    } finally {
      setIsIdentifying(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Audio Library Section */}
      <div className="bg-white rounded-lg shadow-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Audio Library</h2>
          <span className="text-xs text-gray-500">{audioFiles.length} files</span>
        </div>

        {audioFiles.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <FileAudio className="w-12 h-12 mx-auto mb-2" />
            {isLoadingSamples ? (
              <>
                <p className="text-sm">Loading sample audio files...</p>
                <div className="mt-3 flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm">No audio files yet</p>
                <p className="text-xs mt-1">Upload or record audio to get started</p>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {audioFiles.map(file => (
              <div
                key={file.id}
                className={`border-2 rounded-lg p-2 transition-all cursor-pointer ${
                  selectedFile?.id === file.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => onSelectFile(file)}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center space-x-1 min-w-0 flex-1">
                    {selectedFile?.id === file.id && (
                      <Check className="w-3 h-3 text-blue-600 flex-shrink-0" />
                    )}
                    <h3 className="font-medium text-gray-900 text-xs truncate">{file.name}</h3>
                  </div>
                  {!file.isPreloaded && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteFile(file.id);
                      }}
                      className="p-0.5 hover:bg-red-50 rounded transition-colors text-red-600 flex-shrink-0"
                      title="Delete file"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-1 mb-1 flex-wrap">
                  {file.isPreloaded && (
                    <span className="px-1 py-0.5 bg-purple-100 text-purple-800 text-[10px] rounded-full">
                      Sample
                    </span>
                  )}
                  <span className="text-[10px] text-gray-600">{getSampleRateLabel(file.sampleRate)}</span>
                  {file.contentType && (
                    <>
                      <span className="text-[10px] text-gray-400">•</span>
                      <span className="text-[10px] text-gray-600">{getFormatLabel(file.contentType)}</span>
                    </>
                  )}
                  {file.duration && (
                    <>
                      <span className="text-[10px] text-gray-400">•</span>
                      <span className="text-[10px] text-gray-600">{formatDuration(file.duration)}</span>
                    </>
                  )}
                </div>

                {file.url && (
                  <audio src={file.url} controls className="w-full h-6" />
                )}
              </div>
            ))}
          </div>
        )}

        {!selectedFile && (
          <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">💡 Select an audio file from the library above to enable transcription</p>
          </div>
        )}
        
        <div className="flex space-x-2 mt-3">
          <button
            onClick={onTranscribe}
            disabled={!selectedFile || isTranscribing || isIdentifying || !config.serviceUrl}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {isTranscribing ? 'Transcribing...' : 'Transcribe Selected Audio'}
          </button>
          <button
            onClick={handleIdentifyLanguage}
            disabled={!selectedFile || isTranscribing || isIdentifying || !config.serviceUrl}
            className="px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap flex items-center space-x-1"
            title="Identify Language"
          >
            <span>{isIdentifying ? 'Identifying...' : 'Identify Language'}</span>
            <InfoLink 
              href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-speech-language-identification"
              tooltip="Learn about language identification"
            />
          </button>
        </div>

        {lidResult && (
          <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-purple-900 mb-2 flex items-center">
              <span className="mr-1">🌐</span> Language Detection Results:
            </h4>
            {lidResult.results && lidResult.results.length > 0 && lidResult.results[0].language_info ? (
              <div className="space-y-2">
                {lidResult.results[0].language_info.map((lang, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white rounded p-2 border border-purple-100">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-purple-900 text-sm uppercase">{lang.language}</span>
                      </div>
                      {lang.timestamp && (
                        <div className="text-xs text-gray-500 mt-1">
                          Detected at: {lang.timestamp.toFixed(2)}s
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full" 
                          style={{ width: `${(lang.confidence * 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-medium text-purple-700">
                        {(lang.confidence * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-purple-700">No language detection data available</p>
            )}
          </div>
        )}

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Upload Audio File</h2>
        </div>

        <div className="space-y-3">
        <div className="flex gap-4">
          <div className="space-y-3 w-64 flex-shrink-0">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Language
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Sample Rate
                </label>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setSampleRate(SAMPLE_RATE.NARROWBAND)}
                    className={`flex-1 px-2 py-1.5 rounded border text-xs ${
                      sampleRate === SAMPLE_RATE.NARROWBAND
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    8kHz
                  </button>
                  <button
                    onClick={() => setSampleRate(SAMPLE_RATE.BROADBAND)}
                    className={`flex-1 px-2 py-1.5 rounded border text-xs ${
                      sampleRate === SAMPLE_RATE.BROADBAND
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    16kHz
                  </button>
                </div>
              </div>
            </div>

            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors cursor-pointer flex flex-col items-center justify-center flex-1"
            >
              <Upload className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-xs text-gray-600 mb-1">Click to select an audio file</p>
              <p className="text-[10px] text-gray-500">WAV, MP3, FLAC, OGG, WEBM, OPUS</p>
              <p className="text-[10px] text-gray-500">Max {(DEMO_LIMITS.MAX_UPLOAD_SIZE / 1024 / 1024).toFixed(0)}MB, {Math.floor(DEMO_LIMITS.MAX_RECORDING_TIME / 60)} min</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/wav,audio/mp3,audio/mpeg,audio/flac,audio/ogg,audio/webm,audio/opus,.wav,.mp3,.flac,.ogg,.webm,.opus"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2">
              <p className="text-red-800 text-xs">{error}</p>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

// ============================================================================
// FILE: components/LiveLanguageIDPanel.js
// ============================================================================
function LiveLanguageIDPanel({ lidResults }) {
  const FLAG_MAP = {
    'af': '🇿🇦', 'ar': '🇸🇦', 'bg': '🇧🇬', 'bn': '🇧🇩', 'cs': '🇨🇿',
    'cy': '🏴󠁧󠁢󠁷󠁬󠁳󠁿', 'da': '🇩🇰', 'de': '🇩🇪', 'el': '🇬🇷', 'en': '🇬🇧',
    'es': '🇪🇸', 'et': '🇪🇪', 'fa': '🇮🇷', 'fi': '🇫🇮', 'fr': '🇫🇷',
    'ga': '🇮🇪', 'gu': '🇮🇳', 'he': '🇮🇱', 'hi': '🇮🇳', 'hr': '🇭🇷',
    'hu': '🇭🇺', 'hy': '🇦🇲', 'id': '🇮🇩', 'is': '🇮🇸', 'it': '🇮🇹',
    'ja': '🇯🇵', 'ka': '🇬🇪', 'kn': '🇮🇳', 'ko': '🇰🇷', 'lt': '🇱🇹',
    'lv': '🇱🇻', 'mk': '🇲🇰', 'ml': '🇮🇳', 'mr': '🇮🇳', 'ms': '🇲🇾',
    'mt': '🇲🇹', 'nl': '🇳🇱', 'no': '🇳🇴', 'pa': '🇮🇳', 'pl': '🇵🇱',
    'pt': '🇵🇹', 'ro': '🇷🇴', 'ru': '🇷🇺', 'sk': '🇸🇰', 'sl': '🇸🇮',
    'sq': '🇦🇱', 'sr': '🇷🇸', 'sv': '🇸🇪', 'sw': '🇰🇪', 'ta': '🇮🇳',
    'te': '🇮🇳', 'th': '🇹🇭', 'tl': '🇵🇭', 'tr': '🇹🇷', 'uk': '🇺🇦',
    'ur': '🇵🇰', 'vi': '🇻🇳', 'zh': '🇨🇳', 'zu': '🇿🇦'
  };

  const LANG_NAMES = new Intl.DisplayNames(['en'], { type: 'language' });

  const getLanguageName = (code) => {
    try { return LANG_NAMES.of(code) || code.toUpperCase(); } catch (e) { return code.toUpperCase(); }
  };

  const getFlag = (code) => FLAG_MAP[code] || '🌐';

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return { bar: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-50 border-green-200' };
    if (confidence >= 0.7) return { bar: 'bg-yellow-500', text: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200' };
    return { bar: 'bg-red-400', text: 'text-red-700', bg: 'bg-red-50 border-red-200' };
  };

  const formatTimestamp = (ts) => {
    if (ts === undefined || ts === null) return '--';
    const secs = parseFloat(ts);
    const m = Math.floor(secs / 60);
    const s = (secs % 60).toFixed(2);
    return `${m}:${s.padStart(5, '0')}`;
  };

  if (!lidResults || lidResults.length === 0) {
    return (
      <div className="text-center py-6 text-gray-400">
        <div className="text-3xl mb-2">🌍</div>
        <p className="text-xs">Language identification results will appear here as audio is processed</p>
      </div>
    );
  }

  // Aggregate all language_info entries across all results
  const allEntries = lidResults.flatMap(r => r.language_info || []);

  // Compute aggregate confidence per language (average confidence)
  const langMap = {};
  allEntries.forEach(entry => {
    if (!langMap[entry.language]) {
      langMap[entry.language] = { confidences: [], latestTimestamp: entry.timestamp };
    }
    langMap[entry.language].confidences.push(entry.confidence);
    if (entry.timestamp > langMap[entry.language].latestTimestamp) {
      langMap[entry.language].latestTimestamp = entry.timestamp;
    }
  });

  const aggregated = Object.entries(langMap)
    .map(([lang, data]) => ({
      language: lang,
      avgConfidence: data.confidences.reduce((a, b) => a + b, 0) / data.confidences.length,
      latestTimestamp: data.latestTimestamp,
      count: data.confidences.length
    }))
    .sort((a, b) => b.avgConfidence - a.avgConfidence);

  const topLang = aggregated[0];

  return (
    <div className="space-y-3">
      {/* All detected languages */}
      {aggregated.length > 1 && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">All detected languages:</p>
          <div className="space-y-1.5">
            {aggregated.map((lang) => {
              const colors = getConfidenceColor(lang.avgConfidence);
              return (
                <div key={lang.language} className="flex items-center space-x-2 bg-gray-50 rounded-lg px-2.5 py-1.5">
                  <span className="text-base w-6 text-center">{getFlag(lang.language)}</span>
                  <span className="text-xs font-medium text-gray-800 w-28 truncate">{getLanguageName(lang.language)}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-500 ${colors.bar}`}
                      style={{ width: `${lang.avgConfidence * 100}%` }}
                    />
                  </div>
                  <span className={`text-xs font-semibold w-10 text-right ${colors.text}`}>
                    {(lang.avgConfidence * 100).toFixed(0)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent raw results timeline */}
      <div>
        <p className="text-xs font-medium text-gray-600 mb-2">Recent results ({lidResults.length} received):</p>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {[...lidResults].reverse().slice(0, 10).map((result, rIdx) => (
            <div key={rIdx} className="bg-gray-50 border border-gray-200 rounded px-2 py-1">
              {(result.language_info || []).map((entry, eIdx) => (
                <div key={eIdx} className="flex items-center justify-between text-xs">
                  <span className="flex items-center space-x-1">
                    <span>{getFlag(entry.language)}</span>
                    <span className="font-medium">{entry.language.toUpperCase()}</span>
                    <span className="text-gray-500">— {getLanguageName(entry.language)}</span>
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="font-mono text-gray-500">{formatTimestamp(entry.timestamp)}</span>
                    <span className={`font-semibold ${getConfidenceColor(entry.confidence).text}`}>
                      {(entry.confidence * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FILE: components/LiveTab.js
// ============================================================================
function LiveTab({ config, onAddFile, onLiveData, onStreamingStateChange, onSwitchTab, onSelectFile, audioFiles }) {
  const [isRecording, setIsRecording] = useState(false);
  const [savedFileName, setSavedFileName] = useState('');
  const [savedFileId, setSavedFileId] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLIDStreaming, setIsLIDStreaming] = useState(false);
  const [isLIDConnecting, setIsLIDConnecting] = useState(false);
  const [lidResults, setLidResults] = useState([]);
  const [lidError, setLidError] = useState('');
  const [lidStreamingTime, setLidStreamingTime] = useState(0);
  const [sampleRate, setSampleRate] = useState(SAMPLE_RATE.BROADBAND);
  const [language, setLanguage] = useState(config.language);
  const [error, setError] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [streamingTime, setStreamingTime] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const wsServiceRef = useRef(null);
  const wsLIDServiceRef = useRef(null);
  const lidStreamRef = useRef(null);
  const lidProcessorRef = useRef(null);
  const lidTimerRef = useRef(null);
  const lidStartTimeRef = useRef(null);
  const streamRef = useRef(null);
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);

  const handleStartRecording = async () => {
    try {
      setError('');
      setSuccessMessage('');
      const constraints = {
        audio: {
          sampleRate: sampleRate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
        audioBitsPerSecond: sampleRate * 16
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      startTimeRef.current = Date.now();
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setRecordingTime(elapsed);
        
        // Check time limit
        if (elapsed >= DEMO_LIMITS.MAX_RECORDING_TIME) {
          handleStopRecording();
          setError(`Recording stopped: Demo limit of ${Math.floor(DEMO_LIMITS.MAX_RECORDING_TIME / 60)} minutes reached.`);
        }
      }, 1000);

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        clearInterval(timerRef.current);
        
        const webmBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const duration = (Date.now() - startTimeRef.current) / 1000;
        
        // Convert WebM to WAV
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const arrayBuffer = await webmBlob.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          // Convert to WAV
          const wavBlob = audioBufferToWav(audioBuffer);
          
          const currentLanguage = language;
          const currentSampleRate = sampleRate;
          
          const audioFile = {
            id: `recording-${Date.now()}`,
            name: `Recording ${new Date().toLocaleString()}`,
            language: currentLanguage,
            sampleRate: currentSampleRate,
            duration: duration,
            size: wavBlob.size,
            blob: wavBlob,
            url: URL.createObjectURL(wavBlob),
            contentType: 'audio/wav',
            isPreloaded: false
          };

          onAddFile(audioFile);
          setSavedFileName(audioFile.name);
          setSavedFileId(audioFile.id);
          setSuccessMessage('saved');
          
          // Clear success message after 10 seconds
          setTimeout(() => {
            setSuccessMessage('');
            setSavedFileName('');
            setSavedFileId('');
          }, 10000);
        } catch (err) {
          setError('Failed to convert recording: ' + err.message);
        }
        
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorder.start(100);
      setIsRecording(true);
    } catch (err) {
      setError('Failed to start recording: ' + err.message);
      clearInterval(timerRef.current);
    }
  };

  // Helper function to convert AudioBuffer to WAV
  const audioBufferToWav = (audioBuffer) => {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numberOfChannels * bytesPerSample;
    
    const data = [];
    for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
      data.push(audioBuffer.getChannelData(i));
    }
    
    const interleaved = interleave(data);
    const dataLength = interleaved.length * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataLength);
    const view = new DataView(buffer);
    
    // Write WAV header
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, 'data');
    view.setUint32(40, dataLength, true);
    
    // Write audio data
    floatTo16BitPCM(view, 44, interleaved);
    
    return new Blob([buffer], { type: 'audio/wav' });
  };
  
  const interleave = (channelData) => {
    const length = channelData[0].length;
    const result = new Float32Array(length * channelData.length);
    let offset = 0;
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < channelData.length; channel++) {
        result[offset++] = channelData[channel][i];
      }
    }
    return result;
  };
  
  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  const floatTo16BitPCM = (view, offset, input) => {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
      setRecordingTime(0);
    }
  };

  const handleStartStreaming = async () => {
    if (!config.serviceUrl) {
      setError('Please configure service URL first');
      return;
    }

    try {
      setError('');
      setSuccessMessage('');
      setIsConnecting(true);
      setStreamingTime(0);
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          sampleRate: sampleRate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      
      streamRef.current = stream;
      startTimeRef.current = Date.now();

      // Start streaming timer
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setStreamingTime(elapsed);
        
        // Check time limit
        if (elapsed >= DEMO_LIMITS.MAX_STREAMING_TIME) {
          handleStopStreaming();
          setError(`Streaming stopped: Demo limit of ${Math.floor(DEMO_LIMITS.MAX_STREAMING_TIME / 60)} minutes reached.`);
        }
      }, 1000);

      const service = new WatsonSTTService(config.apiKey, config.serviceUrl);
      
      // Use model from config
      const model = config.model;

      const params = {
        speech_detector_sensitivity: config.speech_detector_sensitivity,
        background_audio_suppression: config.background_audio_suppression,
        end_of_phrase_silence_time: config.end_of_phrase_silence_time,
        sad_module: config.sad_module,
        timestamps: config.timestamps,
        speaker_labels: config.speaker_labels,
        interim_results: config.interim_results,
        audio_metrics: config.audio_metrics,
        processing_metrics: config.processing_metrics,
        low_latency: config.low_latency,
        smart_formatting: config.smart_formatting,
        enrichments: config.enrichments,
        sampleRate: sampleRate
      };

      // Wait for WebSocket connection to be established
      await service.connectWebSocket(
        model,
        params,
        (data) => {
          onLiveData(data);
        },
        (error) => {
          console.error('WebSocket error:', error);
          
          let errorMessage = 'WebSocket error occurred';
          
          if (error.message) {
            errorMessage = error.message;
          }
          
          if (error.code && error.code_description) {
            errorMessage = `Error ${error.code}: ${error.code_description} - ${error.message || ''}`;
          }
          
          setError(errorMessage);
          setIsStreaming(false);
          setIsConnecting(false);
          clearInterval(timerRef.current);
          setStreamingTime(0);
          
          // Clean up
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
          }
        }
      );

      wsServiceRef.current = service;

      // Create AudioContext to process raw audio
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: sampleRate
      });
      
      console.log('AudioContext created with sample rate:', audioContext.sampleRate);
      
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      
      console.log('ScriptProcessor created, will send L16 audio data');
      
      processor.onaudioprocess = (e) => {
        if (service.ws && service.ws.readyState === WebSocket.OPEN) {
          const inputData = e.inputBuffer.getChannelData(0);
          // Convert Float32Array to Int16Array (L16 format)
          const int16Data = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            const s = Math.max(-1, Math.min(1, inputData[i]));
            int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }
          service.sendAudioData(int16Data.buffer);
        }
      };
      
      source.connect(processor);
      processor.connect(audioContext.destination);
      
      // Store processor for cleanup
      mediaRecorderRef.current = { processor, audioContext, source };

      setIsConnecting(false);
      setIsStreaming(true);
      if (onStreamingStateChange) onStreamingStateChange(true);
    } catch (err) {
      console.error('Streaming error:', err);
      clearInterval(timerRef.current);
      setStreamingTime(0);
      
      let errorMessage = err.message || 'Failed to start streaming';
      
      // Handle specific error types
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Microphone access denied. Please allow microphone permissions and try again.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone and try again.';
      } else if (err.message && err.message.includes('API')) {
        errorMessage = 'API configuration error: ' + err.message;
      } else if (err.message && err.message.includes('Token')) {
        errorMessage = 'Authentication failed: ' + err.message;
      }
      
      setError(errorMessage);
      setIsStreaming(false);
      setIsConnecting(false);
      if (onStreamingStateChange) onStreamingStateChange(false);
    }
  };

  const handleStopStreaming = () => {
    if (mediaRecorderRef.current) {
      const { processor, audioContext, source } = mediaRecorderRef.current;
      if (processor) {
        source.disconnect();
        processor.disconnect();
      }
      if (audioContext) {
        audioContext.close();
      }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (wsServiceRef.current) {
      wsServiceRef.current.closeWebSocket();
    }
    clearInterval(timerRef.current);
    setStreamingTime(0);
    setIsStreaming(false);
    setIsConnecting(false);
    if (onStreamingStateChange) onStreamingStateChange(false);
  };

  const handleStartLIDStreaming = async () => {
    if (!config.serviceUrl) {
      setLidError('Please configure service URL first');
      return;
    }

    try {
      setLidError('');
      setLidResults([]);
      setIsLIDConnecting(true);
      setLidStreamingTime(0);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: sampleRate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      lidStreamRef.current = stream;
      lidStartTimeRef.current = Date.now();

      // Start LID timer
      lidTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - lidStartTimeRef.current) / 1000);
        setLidStreamingTime(elapsed);
        if (elapsed >= DEMO_LIMITS.MAX_STREAMING_TIME) {
          handleStopLIDStreaming();
          setLidError(`LID streaming stopped: Demo limit of ${Math.floor(DEMO_LIMITS.MAX_STREAMING_TIME / 60)} minutes reached.`);
        }
      }, 1000);

      const service = new WatsonSTTService(config.apiKey, config.serviceUrl);

      await service.connectWebSocketLID(
        { sampleRate },
        (data) => {
          if (data.results && data.results.length > 0) {
            setLidResults(prev => [...prev, ...data.results]);
          }
        },
        (error) => {
          console.error('LID WebSocket error:', error);
          let errorMessage = error.message || 'LID WebSocket error occurred';
          if (error.code && error.code_description) {
            errorMessage = `Error ${error.code}: ${error.code_description} - ${error.message || ''}`;
          }
          setLidError(errorMessage);
          setIsLIDStreaming(false);
          setIsLIDConnecting(false);
          clearInterval(lidTimerRef.current);
          setLidStreamingTime(0);
          if (lidStreamRef.current) {
            lidStreamRef.current.getTracks().forEach(track => track.stop());
          }
        }
      );

      wsLIDServiceRef.current = service;

      // Create AudioContext for raw audio processing
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate });
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);

      processor.onaudioprocess = (e) => {
        if (service.wsLID && service.wsLID.readyState === WebSocket.OPEN) {
          const inputData = e.inputBuffer.getChannelData(0);
          const int16Data = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            const s = Math.max(-1, Math.min(1, inputData[i]));
            int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }
          service.sendLIDAudioData(int16Data.buffer);
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      lidProcessorRef.current = { processor, audioContext, source };

      setIsLIDConnecting(false);
      setIsLIDStreaming(true);
    } catch (err) {
      console.error('LID streaming error:', err);
      clearInterval(lidTimerRef.current);
      setLidStreamingTime(0);

      let errorMessage = err.message || 'Failed to start LID streaming';
      if (err.name === 'NotAllowedError') errorMessage = 'Microphone access denied. Please allow microphone permissions.';
      else if (err.name === 'NotFoundError') errorMessage = 'No microphone found. Please connect a microphone.';

      setLidError(errorMessage);
      setIsLIDStreaming(false);
      setIsLIDConnecting(false);
    }
  };

  const handleStopLIDStreaming = () => {
    if (lidProcessorRef.current) {
      const { processor, audioContext, source } = lidProcessorRef.current;
      if (processor) { source.disconnect(); processor.disconnect(); }
      if (audioContext) audioContext.close();
      lidProcessorRef.current = null;
    }
    if (lidStreamRef.current) {
      lidStreamRef.current.getTracks().forEach(track => track.stop());
      lidStreamRef.current = null;
    }
    if (wsLIDServiceRef.current) {
      wsLIDServiceRef.current.closeWebSocketLID();
      wsLIDServiceRef.current = null;
    }
    clearInterval(lidTimerRef.current);
    setLidStreamingTime(0);
    setIsLIDStreaming(false);
    setIsLIDConnecting(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="space-y-2.5">
        <div className="border border-gray-200 rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <span className="bg-blue-100 text-blue-700 rounded-full w-5 h-5 flex items-center justify-center mr-2 text-xs flex-shrink-0">1</span>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Live Streaming Transcription</h3>
                <p className="text-xs text-gray-600">Real-time transcription (max {Math.floor(DEMO_LIMITS.MAX_STREAMING_TIME / 60)} min)</p>
              </div>
            </div>
            {!isStreaming && !isConnecting ? (
              <button
                onClick={handleStartStreaming}
                disabled={isRecording}
                className="flex items-center space-x-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 py-1.5 rounded text-xs transition-colors disabled:opacity-50 flex-shrink-0"
              >
                <Mic className="w-3.5 h-3.5" />
                <Play className="w-3.5 h-3.5" />
                <span>Start</span>
              </button>
            ) : isConnecting ? (
              <button
                disabled
                className="flex items-center space-x-1.5 bg-gray-600 text-white font-medium px-3 py-1.5 rounded text-xs flex-shrink-0"
              >
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                <span>Connecting...</span>
              </button>
            ) : (
              <button
                onClick={handleStopStreaming}
                className="flex items-center space-x-1.5 bg-red-500 hover:bg-red-600 text-white font-medium px-3 py-1.5 rounded text-xs transition-colors flex-shrink-0"
              >
                <Pause className="w-3.5 h-3.5" />
                <span>Stop</span>
              </button>
            )}
          </div>
          {(isConnecting || isStreaming) && (
            <div className="flex items-center justify-between text-xs text-gray-600 bg-blue-50 rounded px-2 py-1.5">
              <span className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                <span>{isConnecting ? 'Connecting...' : 'Streaming to Watson'}</span>
              </span>
              {isStreaming && <span>{formatTime(streamingTime)} / {formatTime(DEMO_LIMITS.MAX_STREAMING_TIME)}</span>}
            </div>
          )}
        </div>

        {/* Section 2: Live Language Identification */}
        <div className="border border-gray-200 rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <span className="bg-purple-100 text-purple-700 rounded-full w-5 h-5 flex items-center justify-center mr-2 text-xs flex-shrink-0">2</span>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Live Language Identification</h3>
                <p className="text-xs text-gray-600">Real-time language detection (max {Math.floor(DEMO_LIMITS.MAX_STREAMING_TIME / 60)} min)</p>
              </div>
            </div>
            {!isLIDStreaming && !isLIDConnecting ? (
              <button
                onClick={handleStartLIDStreaming}
                disabled={isRecording || isStreaming}
                className="flex items-center space-x-1.5 bg-purple-600 hover:bg-purple-700 text-white font-medium px-3 py-1.5 rounded text-xs transition-colors disabled:opacity-50 flex-shrink-0"
              >
                <Mic className="w-3.5 h-3.5" />
                <Globe className="w-3.5 h-3.5" />
                <span>Start</span>
              </button>
            ) : isLIDConnecting ? (
              <button
                disabled
                className="flex items-center space-x-1.5 bg-gray-600 text-white font-medium px-3 py-1.5 rounded text-xs flex-shrink-0"
              >
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                <span>Connecting...</span>
              </button>
            ) : (
              <button
                onClick={handleStopLIDStreaming}
                className="flex items-center space-x-1.5 bg-red-500 hover:bg-red-600 text-white font-medium px-3 py-1.5 rounded text-xs transition-colors flex-shrink-0"
              >
                <Pause className="w-3.5 h-3.5" />
                <span>Stop</span>
              </button>
            )}
          </div>

          {(isLIDConnecting || isLIDStreaming) && (
            <div className="flex items-center justify-between text-xs text-gray-600 bg-purple-50 rounded px-2 py-1.5 mb-2">
              <span className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-pulse"></div>
                <span>{isLIDConnecting ? 'Connecting...' : 'Streaming to Watson LID'}</span>
              </span>
              {isLIDStreaming && <span>{formatTime(lidStreamingTime)} / {formatTime(DEMO_LIMITS.MAX_STREAMING_TIME)}</span>}
            </div>
          )}

          {(lidResults.length > 0 || isLIDStreaming) && (
            <div className="border border-gray-200 rounded p-2">
              <div className="flex items-center justify-between mb-1.5">
                <h4 className="text-xs font-semibold text-gray-700">Detection Results</h4>
                {lidResults.length > 0 && (
                  <button
                    onClick={() => setLidResults([])}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Clear
                  </button>
                )}
              </div>
              <LiveLanguageIDPanel lidResults={lidResults} />
            </div>
          )}

          {lidError && (
            <div className="bg-red-50 border border-red-200 rounded px-2 py-1.5 flex items-center space-x-1.5">
              <span className="text-red-600 text-sm flex-shrink-0">⚠️</span>
              <p className="text-xs text-red-800">{lidError}</p>
            </div>
          )}
        </div>

        {/* Section 3: Record & Save */}
        <div className="border border-gray-200 rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center flex-1">
              <span className="bg-red-100 text-red-700 rounded-full w-5 h-5 flex items-center justify-center mr-2 text-xs flex-shrink-0">3</span>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900">Record & Save</h3>
                <p className="text-xs text-gray-600">Record audio and save as WAV (max {Math.floor(DEMO_LIMITS.MAX_RECORDING_TIME / 60)} min)</p>
              </div>
            </div>
            {!isRecording ? (
              <button
                onClick={handleStartRecording}
                disabled={isStreaming}
                className="flex items-center space-x-1.5 bg-red-500 hover:bg-red-600 text-white font-medium px-3 py-1.5 rounded text-xs transition-colors disabled:opacity-50 flex-shrink-0"
              >
                <Mic className="w-3.5 h-3.5" />
                <Download className="w-3.5 h-3.5" />
                <span>Start</span>
              </button>
            ) : (
              <button
                onClick={handleStopRecording}
                className="flex items-center space-x-1.5 bg-gray-700 hover:bg-gray-800 text-white font-medium px-3 py-1.5 rounded text-xs transition-colors flex-shrink-0"
              >
                <Pause className="w-3.5 h-3.5" />
                <span>Stop & Save</span>
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-3 mb-2">
            <div className="flex items-center space-x-1.5 flex-1">
              <label className="text-xs font-medium text-gray-700">Lang:</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={isRecording || isStreaming}
                className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-1.5">
              <label className="text-xs font-medium text-gray-700">Quality:</label>
              <div className="flex space-x-1">
                <button
                  onClick={() => setSampleRate(SAMPLE_RATE.NARROWBAND)}
                  disabled={isRecording || isStreaming}
                  className={`px-2 py-1 rounded border text-xs ${
                    sampleRate === SAMPLE_RATE.NARROWBAND
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-gray-400'
                  } disabled:opacity-50`}
                >
                  8kHz
                </button>
                <button
                  onClick={() => setSampleRate(SAMPLE_RATE.BROADBAND)}
                  disabled={isRecording || isStreaming}
                  className={`px-2 py-1 rounded border text-xs ${
                    sampleRate === SAMPLE_RATE.BROADBAND
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-gray-400'
                  } disabled:opacity-50`}
                >
                  16kHz
                </button>
              </div>
            </div>
          </div>

          {isRecording && (
            <div className="flex items-center justify-between text-xs text-gray-600 bg-red-50 rounded px-2 py-1.5">
              <span className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span>Recording...</span>
              </span>
              <span>{formatTime(recordingTime)} / {formatTime(DEMO_LIMITS.MAX_RECORDING_TIME)}</span>
            </div>
          )}

          {successMessage && savedFileName && (
            <div className="bg-green-50 border border-green-200 rounded px-2 py-1.5 flex items-center justify-between">
              <span className="text-xs text-green-800 font-medium">✓ Saved: {savedFileName}</span>
              <button
                onClick={() => {
                  if (onSwitchTab && onSelectFile && savedFileId && audioFiles) {
                    const fileToSelect = audioFiles.find(f => f.id === savedFileId);
                    if (fileToSelect) {
                      onSelectFile(fileToSelect);
                    }
                    onSwitchTab('audio');
                  }
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
              >
                Go to Audio Files →
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded px-2 py-1.5 flex items-center space-x-1.5">
            <span className="text-red-600 text-sm flex-shrink-0">⚠️</span>
            <p className="text-xs text-red-800">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// FILE: components/AudioMetricsPanel.js
// ============================================================================
function AudioMetricsPanel({ audioMetrics }) {
  // Handle both array format (from WebSocket) and single object format (from REST API)
  const metricsArray = Array.isArray(audioMetrics) ? audioMetrics : [audioMetrics];
  
  if (!audioMetrics || metricsArray.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">📊 Audio Metrics</h3>
          <InfoLink
            href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-metrics#audio-metrics"
            tooltip="Learn about audio metrics"
          />
        </div>
        <p className="text-gray-400 text-center py-6 text-xs">
          Audio metrics will appear here when available
        </p>
      </div>
    );
  }

  // Calculate total clipping percentage from clipping_rate array
  const calculateClippingRate = (clippingRateArray) => {
    if (!Array.isArray(clippingRateArray) || clippingRateArray.length === 0) return null;
    const totalCount = clippingRateArray.reduce((sum, item) => sum + item.count, 0);
    if (totalCount === 0) return 0;
    // Sum counts for bins above 0.00001 (significant clipping)
    const clippedCount = clippingRateArray.slice(1).reduce((sum, item) => sum + item.count, 0);
    return (clippedCount / totalCount) * 100;
  };

  // Get quality assessment based on audio quality (SNR and Clipping), not speech content
  const getQualityIndicator = (snr, speechRatio, clippingRate) => {
    // Excellent: High SNR, minimal clipping
    if (snr >= 40 && clippingRate < 1) {
      return { label: 'Excellent', color: 'green', icon: '✓' };
    }
    // Good: Decent SNR, low clipping
    else if (snr >= 25 && clippingRate < 5) {
      return { label: 'Good', color: 'blue', icon: '✓' };
    }
    // Fair: Moderate SNR, acceptable clipping
    else if (snr >= 15 && clippingRate < 10) {
      return { label: 'Fair', color: 'yellow', icon: '⚠' };
    }
    // Poor: Low SNR or high clipping
    else {
      return { label: 'Poor', color: 'red', icon: '✗' };
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">📊 Audio Metrics</h3>
        <InfoLink
          href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-metrics#audio-metrics"
          tooltip="Learn about audio metrics"
        />
      </div>
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {metricsArray.map((metric, idx) => {
          const snr = metric.accumulated?.signal_to_noise_ratio;
          const speechRatio = metric.accumulated?.speech_ratio;
          const clippingRate = metric.accumulated?.clipping_rate ? calculateClippingRate(metric.accumulated.clipping_rate) : 0;
          const quality = getQualityIndicator(snr, speechRatio, clippingRate);
          
          return (
            <div key={idx} className="space-y-2">
              {/* Overall Quality Badge */}
              {snr !== undefined && speechRatio !== undefined && (
                <div className={`bg-${quality.color}-50 border border-${quality.color}-200 rounded p-1.5 text-center`}>
                  <span className="text-xs font-semibold text-${quality.color}-800">
                    {quality.icon} Quality: {quality.label}
                  </span>
                </div>
              )}
              
              {/* Metrics Table - 2 columns, 3 rows */}
              <div className="border border-gray-200 rounded overflow-hidden">
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b border-gray-200">
                      {metric.accumulated?.end_time !== undefined && (
                        <>
                          <td className="px-2 py-1.5 bg-gray-50 text-gray-600 font-medium w-1/4">Duration</td>
                          <td className="px-2 py-1.5 text-right font-semibold text-gray-900 w-1/4">
                            {metric.accumulated.end_time.toFixed(1)}s
                          </td>
                        </>
                      )}
                      {snr !== undefined && (
                        <>
                          <td className="px-2 py-1.5 bg-green-50 text-gray-700 font-medium w-1/4 border-l border-gray-200">SNR</td>
                          <td className="px-2 py-1.5 bg-green-50 text-right font-bold text-green-700 w-1/4">{snr.toFixed(1)} dB</td>
                        </>
                      )}
                    </tr>
                    <tr className="border-b border-gray-200">
                      {speechRatio !== undefined && (
                        <>
                          <td className="px-2 py-1.5 bg-blue-50 text-gray-700 font-medium">Speech Ratio</td>
                          <td className="px-2 py-1.5 bg-blue-50 text-right font-bold text-blue-700">{(speechRatio * 100).toFixed(1)}%</td>
                        </>
                      )}
                      {metric.accumulated?.clipping_rate !== undefined && (
                        <>
                          <td className={`px-2 py-1.5 text-gray-700 font-medium border-l border-gray-200 ${clippingRate > 1 ? 'bg-red-50' : 'bg-green-50'}`}>Clipping</td>
                          <td className={`px-2 py-1.5 text-right font-bold ${clippingRate > 1 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                            {clippingRate.toFixed(2)}%
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      {metric.accumulated?.high_frequency_loss !== undefined && (
                        <>
                          <td className="px-2 py-1.5 bg-gray-50 text-gray-700 font-medium">HF Loss</td>
                          <td className="px-2 py-1.5 bg-gray-50 text-right font-semibold text-gray-900">{metric.accumulated.high_frequency_loss.toFixed(2)}</td>
                        </>
                      )}
                      <td className="px-2 py-1.5 bg-gray-50 border-l border-gray-200" colSpan="2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// FILE: components/ProcessingMetricsPanel.js
// ============================================================================
function ProcessingMetricsPanel({ processingMetrics }) {
  if (!processingMetrics || processingMetrics.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">⚙️ Processing Metrics</h3>
          <InfoLink
            href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-metrics#processing-metrics"
            tooltip="Learn about processing metrics"
          />
        </div>
        <p className="text-gray-400 text-center py-6 text-xs">
          Processing metrics will appear here during live transcription
        </p>
      </div>
    );
  }

  // Get the latest processing metric
  const latestMetric = processingMetrics[processingMetrics.length - 1];
  
  // Extract processed_audio values
  const processedAudio = latestMetric.processed_audio || {};
  const wallClock = latestMetric.wall_clock_since_first_byte_received;

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">⚙️ Processing Metrics</h3>
        <InfoLink
          href="https://cloud.ibm.com/docs/speech-to-text?topic=speech-to-text-metrics#processing-metrics"
          tooltip="Learn about processing metrics"
        />
      </div>
      
      <div className="space-y-2">
        {/* Metrics Table - 2 columns, 2 rows */}
        <div className="border border-gray-200 rounded overflow-hidden">
          <table className="w-full text-xs">
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="px-2 py-1.5 bg-gray-50 text-gray-600 font-medium w-1/4">Received</td>
                <td className="px-2 py-1.5 text-right font-semibold text-gray-900 w-1/4">
                  {typeof processedAudio.received === 'number' ? processedAudio.received.toFixed(2) : '0.00'}s
                </td>
                <td className="px-2 py-1.5 bg-gray-50 text-gray-600 font-medium w-1/4 border-l border-gray-200">Seen by Engine</td>
                <td className="px-2 py-1.5 text-right font-semibold text-gray-900 w-1/4">
                  {typeof processedAudio.seen_by_engine === 'number' ? processedAudio.seen_by_engine.toFixed(2) : '0.00'}s
                </td>
              </tr>
              <tr>
                <td className="px-2 py-1.5 bg-gray-50 text-gray-600 font-medium">Transcription</td>
                <td className="px-2 py-1.5 text-right font-semibold text-gray-900">
                  {typeof processedAudio.transcription === 'number' ? processedAudio.transcription.toFixed(2) : '0.00'}s
                </td>
                <td className="px-2 py-1.5 bg-gray-50 text-gray-600 font-medium border-l border-gray-200">Wall Clock</td>
                <td className="px-2 py-1.5 text-right font-semibold text-gray-900">
                  {typeof wallClock === 'number' ? wallClock.toFixed(2) : '0.00'}s
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Metrics count */}
        {processingMetrics.length > 1 && (
          <p className="text-[10px] text-gray-500 text-center">
            {processingMetrics.length} metrics received (interval: 0.5s)
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// FILE: components/EnrichedResultsPanel.js
// ============================================================================
function EnrichedResultsPanel({ enrichedData }) {
  if (!enrichedData || !enrichedData.transcript) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">LLM Speech Enrichment</h3>
        <p className="text-gray-400 text-center py-6 text-xs">
          LLM-enriched results will appear here when available
        </p>
      </div>
    );
  }

  const formatTimestamp = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(2);
    return `${mins}:${secs.padStart(5, '0')}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">LLM Speech Enrichment</h3>
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        <div className="border-l-4 border-indigo-500 pl-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-gray-900">Enriched Transcript</h4>
            {enrichedData.status && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                enrichedData.status === 'success' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {enrichedData.status}
              </span>
            )}
          </div>
          
          {/* Show which enrichments were applied */}
          {enrichedData.enrichments && enrichedData.enrichments.length > 0 && 
           !enrichedData.enrichments.includes('none') && (
            <div className="mb-2 flex flex-wrap gap-1">
              {enrichedData.enrichments.map((enrichment, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs">
                  {enrichment}
                </span>
              ))}
            </div>
          )}
          
          {/* Timestamp range */}
          {enrichedData.timestamp && (
            <div className="mb-2 text-xs text-gray-600">
              <span className="font-medium">Time Range:</span>
              <span className="ml-1 font-mono bg-gray-100 px-2 py-0.5 rounded">
                {formatTimestamp(enrichedData.timestamp.from)} - {formatTimestamp(enrichedData.timestamp.to)}
              </span>
            </div>
          )}
          
          {/* Enriched transcript text */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
            <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
              {enrichedData.transcript}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FILE: components/StatisticsPanel.js
// ============================================================================
function StatisticsPanel({ results, audioMetrics, startTime, endTime, isLive }) {
  const calculateStats = () => {
    if (results.length === 0) return null;

    const allWords = results.flatMap(r => 
      r.alternatives?.[0]?.timestamps?.map(t => t[0]) || []
    );
    
    const wordCount = allWords.length;
    const uniqueWords = new Set(allWords.map(w => w.toLowerCase())).size;
    
    const durations = results.map(r => {
      const timestamps = r.alternatives?.[0]?.timestamps;
      if (!timestamps || timestamps.length === 0) return 0;
      return timestamps[timestamps.length - 1][2] - timestamps[0][1];
    });
    
    const totalDuration = durations.reduce((sum, d) => sum + d, 0);
    const avgConfidence = results.reduce((sum, r) => 
      sum + (r.alternatives?.[0]?.confidence || 0), 0) / results.length;
    
    const wordsPerMinute = totalDuration > 0 ? (wordCount / totalDuration) * 60 : 0;
    
    const timeRange = results.reduce((range, r) => {
      const timestamps = r.alternatives?.[0]?.timestamps;
      if (!timestamps || timestamps.length === 0) return range;
      const start = timestamps[0][1];
      const end = timestamps[timestamps.length - 1][2];
      return {
        min: Math.min(range.min, start),
        max: Math.max(range.max, end)
      };
    }, { min: Infinity, max: -Infinity });
    
    return {
      wordCount,
      uniqueWords,
      totalDuration: totalDuration.toFixed(1),
      avgConfidence: (avgConfidence * 100).toFixed(1),
      wordsPerMinute: wordsPerMinute.toFixed(0),
      segments: results.length,
      timeRange: timeRange.max > timeRange.min ? 
        `${timeRange.min.toFixed(1)}s - ${timeRange.max.toFixed(1)}s` : 'N/A'
    };
  };

  const stats = calculateStats();

  if (!stats) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">📊 Statistics</h3>
        <p className="text-gray-400 text-center py-4 text-xs">Statistics will appear here</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">📊 Statistics</h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
          <div className="text-xs text-blue-600 font-medium mb-1">Total Words</div>
          <div className="text-2xl font-bold text-blue-900">{stats.wordCount}</div>
          <div className="text-xs text-blue-600 mt-1">{stats.uniqueWords} unique</div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border border-green-200">
          <div className="text-xs text-green-600 font-medium mb-1">Avg Confidence</div>
          <div className="text-2xl font-bold text-green-900">{stats.avgConfidence}%</div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
          <div className="text-xs text-purple-600 font-medium mb-1">Speaking Rate</div>
          <div className="text-2xl font-bold text-purple-900">{stats.wordsPerMinute}</div>
          <div className="text-xs text-purple-600 mt-1">words/min</div>
        </div>
        
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-3 border border-orange-200">
          <div className="text-xs text-orange-600 font-medium mb-1">Segments</div>
          <div className="text-2xl font-bold text-orange-900">{stats.segments}</div>
          <div className="text-xs text-orange-600 mt-1">{stats.totalDuration}s total</div>
        </div>
      </div>
      
      
      {isLive && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-center space-x-2 text-red-600">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-xs font-semibold">LIVE</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// FILE: components/PerformanceMetrics.js
// ============================================================================
function PerformanceMetrics({ metrics }) {
  if (!metrics) return null;

  const getRTFColor = (rtf) => {
    if (rtf === 'N/A') return 'text-gray-600';
    const val = parseFloat(rtf);
    if (val < 0.3) return 'text-green-600';
    if (val < 0.7) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRTFLabel = (rtf) => {
    if (rtf === 'N/A') return 'N/A';
    const val = parseFloat(rtf);
    if (val < 0.3) return 'Excellent';
    if (val < 0.7) return 'Good';
    return 'Slow';
  };

  return (
    <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-gray-900 flex items-center">
          <span className="mr-1">⚡</span> Performance
        </h4>
        <span className={`text-xs font-semibold ${getRTFColor(metrics.realTimeFactor)}`}>
          {getRTFLabel(metrics.realTimeFactor)}
        </span>
      </div>
      
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-gray-600 mb-0.5">Processing</div>
          <div className="font-semibold text-gray-900">{metrics.processingTime}s</div>
        </div>
        <div>
          <div className="text-gray-600 mb-0.5">Audio Length</div>
          <div className="font-semibold text-gray-900">{metrics.audioDuration}s</div>
        </div>
        <div>
          <div className="text-gray-600 mb-0.5">RTF</div>
          <div className={`font-semibold ${getRTFColor(metrics.realTimeFactor)}`}>
            {metrics.realTimeFactor}x
          </div>
        </div>
      </div>
      
      <div className="mt-2 pt-2 border-t border-green-200">
        <div className="text-xs text-gray-500">
          Real-Time Factor (RTF) = Processing Time / Audio Duration
          <br />
          Lower is better. &lt;1.0 means faster than real-time.
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FILE: components/QuickSettingsPanel.js
// ============================================================================
function QuickSettingsPanel({ config, onConfigChange }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-sm font-semibold text-gray-900"
      >
        <span>⚡ Quick Settings</span>
        <span className="text-gray-400">{isExpanded ? '▼' : '▶'}</span>
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-3 pt-3 border-t border-gray-200">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Model</label>
            <select
              value={config.model}
              onChange={(e) => onConfigChange({ ...config, model: e.target.value })}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {MODELS.map(model => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">End of Phrase Silence Time</label>
            <input
              type="number"
              step="0.1"
              min="0.0"
              max="2.0"
              value={config.end_of_phrase_silence_time}
              onChange={(e) => onConfigChange({ ...config, end_of_phrase_silence_time: parseFloat(e.target.value) })}
              placeholder="0.0 - 2.0"
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-0.5">Range: 0.0 to 2.0 (default: 0.8)</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Background Audio Suppression</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={config.background_audio_suppression}
              onChange={(e) => onConfigChange({ ...config, background_audio_suppression: parseFloat(e.target.value) })}
              placeholder="0.0 - 1.0"
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-0.5">Range: 0.0 to 1.0</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Speech Detector Sensitivity</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={config.speech_detector_sensitivity}
              onChange={(e) => onConfigChange({ ...config, speech_detector_sensitivity: parseFloat(e.target.value) })}
              placeholder="0.0 - 1.0"
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-0.5">Range: 0.0 to 1.0</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">SAD Module</label>
            <select
              value={config.sad_module}
              onChange={(e) => onConfigChange({ ...config, sad_module: e.target.value })}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Default</option>
              <option value="1">1 - Legacy</option>
              <option value="2">2 - Next-gen</option>
            </select>
            <p className="text-xs text-gray-500 mt-0.5">Speech Activity Detection</p>
          </div>
        </div>
      )}
    </div>
  );
}
function TranscriptPanel({ title, results, isFinal, speakerLabels }) {
  const scrollRef = useRef(null);
  const [showWordDetails, setShowWordDetails] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [results, autoScroll]);

  const formatTimestamp = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(2);
    return `${mins}:${secs.padStart(5, '0')}`;
  };

  const formatClockTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--:--';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getConfidenceColor = (confidence) => {
    if (!confidence) return 'bg-gray-100 text-gray-700';
    if (confidence >= 0.9) return 'bg-green-100 text-green-800';
    if (confidence >= 0.75) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getConfidenceLabel = (confidence) => {
    if (!confidence) return 'N/A';
    if (confidence >= 0.9) return 'High';
    if (confidence >= 0.75) return 'Med';
    return 'Low';
  };

  const getSpeakerColor = (speaker) => {
    const colors = [
      'border-blue-500 bg-blue-50',
      'border-green-500 bg-green-50',
      'border-purple-500 bg-purple-50',
      'border-orange-500 bg-orange-50',
      'border-pink-500 bg-pink-50',
      'border-indigo-500 bg-indigo-50',
    ];
    const speakerNum = parseInt(speaker.replace('spk_', ''));
    return colors[speakerNum % colors.length];
  };

  const hasSpeakerLabels = speakerLabels && speakerLabels.length > 0;

  const mergeResultsWithSpeakers = () => {
    if (!hasSpeakerLabels) {
      return results.map((result, idx) => ({
        transcript: result.alternatives?.[0]?.transcript || '',
        startTime: result.alternatives?.[0]?.timestamps?.[0]?.[1],
        endTime: result.alternatives?.[0]?.timestamps?.[result.alternatives[0].timestamps.length - 1]?.[2],
        confidence: result.alternatives?.[0]?.confidence,
        timestamps: result.alternatives?.[0]?.timestamps,
        speaker: null,
        key: idx
      }));
    }

    const merged = [];
    
    results.forEach((result, idx) => {
      const transcript = result.alternatives?.[0]?.transcript || '';
      const timestamps = result.alternatives?.[0]?.timestamps;
      const confidence = result.alternatives?.[0]?.confidence;
      
      if (!timestamps || timestamps.length === 0) {
        merged.push({
          transcript,
          startTime: null,
          endTime: null,
          confidence,
          timestamps: [],
          speaker: null,
          key: idx
        });
        return;
      }

      const startTime = timestamps[0][1];
      const endTime = timestamps[timestamps.length - 1][2];
      
      const relevantLabels = speakerLabels.filter(label => 
        label.from <= endTime && label.to >= startTime
      );
      
      let dominantSpeaker = null;
      if (relevantLabels.length > 0) {
        const speakerDurations = {};
        relevantLabels.forEach(label => {
          const overlapStart = Math.max(label.from, startTime);
          const overlapEnd = Math.min(label.to, endTime);
          const duration = overlapEnd - overlapStart;
          
          speakerDurations[label.speaker] = (speakerDurations[label.speaker] || 0) + duration;
        });
        
        dominantSpeaker = Object.entries(speakerDurations)
          .sort((a, b) => b[1] - a[1])[0]?.[0];
      }
      
      merged.push({
        transcript,
        startTime,
        endTime,
        confidence,
        timestamps,
        speaker: dominantSpeaker,
        key: idx
      });
    });

    return merged;
  };

  const mergedResults = mergeResultsWithSpeakers();

  const copyTranscript = () => {
    const text = mergedResults.map(item => {
      const time = formatTimestamp(item.startTime);
      const speaker = item.speaker ? `[${item.speaker.toUpperCase()}]` : '';
      return `${time} ${speaker} ${item.transcript}`;
    }).join('\n');
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center space-x-2">
          {results.length > 0 && (
            <>
              <button
                onClick={copyTranscript}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                title="Copy transcript"
              >
                Copy
              </button>
              <button
                onClick={() => setShowWordDetails(!showWordDetails)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                title="Toggle word details"
              >
                {showWordDetails ? 'Hide' : 'Show'} Words
              </button>
              <button
                onClick={() => setAutoScroll(!autoScroll)}
                className={`text-xs font-medium ${autoScroll ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-gray-600'}`}
                title="Toggle auto-scroll"
              >
                {autoScroll ? '🔄' : '⏸️'}
              </button>
              <span className="text-xs text-gray-500">{results.length}</span>
            </>
          )}
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            isFinal ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {isFinal ? 'Final' : 'Interim'}
          </span>
        </div>
      </div>

      {results.length === 0 ? (
        <p className="text-gray-400 text-center py-4 text-xs">
          {isFinal ? 'Final transcripts will appear here' : 'Interim results will appear here'}
        </p>
      ) : (
        <div ref={scrollRef} className="space-y-2 max-h-[400px] overflow-y-auto scroll-smooth">
          {mergedResults.map((item, idx) => (
            <div 
              key={item.key} 
              className={`border-l-4 rounded-r-lg p-2.5 transition-all ${
                hasSpeakerLabels 
                  ? (item.speaker ? getSpeakerColor(item.speaker) : 'border-gray-400 bg-gray-50') 
                  : (isFinal ? 'border-blue-500 bg-blue-50' : 'border-yellow-400 bg-yellow-50 opacity-75')
              } ${idx === mergedResults.length - 1 && !isFinal ? 'ring-2 ring-yellow-300' : ''}`}
            >
              {/* Timeline header */}
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                  {/* Clock time */}
                  {item.startTime !== null && (
                    <span className="font-mono text-xs font-semibold text-gray-700 bg-white px-2 py-1 rounded shadow-sm border border-gray-300">
                      ⏱️ {formatClockTime(item.startTime)}
                    </span>
                  )}
                  
                  {/* Relative time */}
                  {item.startTime !== null && item.endTime !== null && (
                    <span className="font-mono text-xs text-gray-600 bg-white px-2 py-1 rounded shadow-sm">
                      {formatTimestamp(item.startTime)} → {formatTimestamp(item.endTime)}
                    </span>
                  )}
                  
                  {/* Speaker */}
                  {hasSpeakerLabels && (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                      item.speaker ? 'bg-white shadow-sm' : 'bg-gray-100 text-gray-500'
                    }`}>
                      🎤 {item.speaker ? item.speaker.toUpperCase() : 'Unknown'}
                    </span>
                  )}
                  
                  {/* Duration */}
                  {item.startTime !== null && item.endTime !== null && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {(item.endTime - item.startTime).toFixed(1)}s
                    </span>
                  )}
                </div>
                
                {/* Confidence badge */}
                {item.confidence && (
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${getConfidenceColor(item.confidence)}`}>
                    {getConfidenceLabel(item.confidence)} {(item.confidence * 100).toFixed(0)}%
                  </span>
                )}
              </div>
              
              {/* Transcript text */}
              <p className={`text-sm leading-relaxed ${isFinal ? 'text-gray-900 font-medium' : 'text-gray-700 italic'}`}>
                {item.transcript}
              </p>
              
              {/* Word-level details */}
              {showWordDetails && item.timestamps && item.timestamps.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="flex flex-wrap gap-1">
                    {item.timestamps.map((word, widx) => (
                      <span 
                        key={widx}
                        className="inline-flex items-center bg-white border border-gray-300 rounded px-1.5 py-0.5 text-xs"
                        title={`${formatTimestamp(word[1])} - ${formatTimestamp(word[2])}`}
                      >
                        <span className="font-medium">{word[0]}</span>
                        <span className="ml-1 text-gray-400 font-mono text-[10px]">
                          {formatTimestamp(word[1])}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// FILE: components/LoadingSpinner.js
// ============================================================================
function LoadingSpinner({ message = 'Loading...', size = 'medium' }) {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-8 h-8',
    large: 'w-12 h-12'
  };

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className={`animate-spin rounded-full border-b-2 border-blue-600 ${sizeClasses[size]}`}></div>
      <p className="mt-4 text-sm text-gray-600">{message}</p>
    </div>
  );
}

// ============================================================================
// FILE: components/FullPageLoader.js
// ============================================================================
function FullPageLoader({ message = 'Initializing Watson STT Demo...' }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md mx-4">
        <div className="flex flex-col items-center space-y-6">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <Mic className="w-8 h-8 text-white" />
          </div>
          
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Watson Speech to Text</h2>
            <p className="text-sm text-gray-600">{message}</p>
          </div>
          
          <div className="flex flex-col items-center space-y-3 w-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            
            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 h-1.5 rounded-full animate-pulse" 
                   style={{ width: '60%' }}></div>
            </div>
          </div>
          
          <div className="text-xs text-gray-500 text-center space-y-1">
            <p>• Loading application resources</p>
            <p>• Connecting to backend services</p>
            <p>• Preparing audio samples</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FILE: App.js
// ============================================================================
export default function WatsonSTTApp() {
  const [activeTab, setActiveTab] = useState(TABS.SETTINGS);
  const [config, setConfig] = useState({
    apiKey: '',
    region: 'us-east',
    serviceUrl: 'https://api.us-east.speech-to-text.watson.cloud.ibm.com',
    model: 'en-US',
    speech_detector_sensitivity: 0.7,
    background_audio_suppression: 0.0,
    end_of_phrase_silence_time: 0.5,
    sad_module: '2',
    smart_formatting: 'none',
    enrichments: ['none'],
    timestamps: true,
    speaker_labels: false,
    interim_results: true,
    audio_metrics: false,
    processing_metrics: false,
    low_latency: false
  });
  const [audioFiles, setAudioFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoadingSamples, setIsLoadingSamples] = useState(true);
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({
    backend: false,
    samples: false,
    stories: false
  });
  
  // Cache results per tab
  const [tabResults, setTabResults] = useState({
    [TABS.AUDIO]: {
      finalResults: [],
      interimResults: [],
      speakerLabels: [],
      audioMetrics: [],
      enrichedResults: {},
      transcriptionParams: null,
      transcriptionError: null,
      rawResponse: null,
      performanceMetrics: null
    },
    [TABS.LIVE]: {
      finalResults: [],
      interimResults: [],
      speakerLabels: [],
      audioMetrics: [],
      enrichedResults: {},
      transcriptionParams: null,
      transcriptionError: null,
      rawResponse: null,
      performanceMetrics: null
    }
  });
  
  // Current active results (derived from tabResults based on activeTab)
  const finalResults = tabResults[activeTab]?.finalResults || [];
  const interimResults = tabResults[activeTab]?.interimResults || [];
  const speakerLabels = tabResults[activeTab]?.speakerLabels || [];
  const audioMetrics = tabResults[activeTab]?.audioMetrics || [];
  const processingMetrics = tabResults[activeTab]?.processingMetrics || [];
  const enrichedResults = tabResults[activeTab]?.enrichedResults || {};
  const transcriptionParams = tabResults[activeTab]?.transcriptionParams || null;
  const transcriptionError = tabResults[activeTab]?.transcriptionError || null;
  const rawResponse = tabResults[activeTab]?.rawResponse || null;
  const performanceMetrics = tabResults[activeTab]?.performanceMetrics || null;
  
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isLiveStreaming, setIsLiveStreaming] = useState(false);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);

  // Helper function to update results for current tab
  const updateTabResults = (updates) => {
    setTabResults(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        ...updates
      }
    }));
  };

  // Stories state
  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [isLoadingStories, setIsLoadingStories] = useState(true);
  const [comparisonResults, setComparisonResults] = useState([]);
  const [isRunningStory, setIsRunningStory] = useState(false);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ignore if typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }

      // Cmd/Ctrl + K - Clear transcripts
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        clearTranscripts();
      }
      
      // Cmd/Ctrl + E - Export
      if ((e.metaKey || e.ctrlKey) && e.key === 'e') {
        e.preventDefault();
        if (finalResults.length > 0) {
          // Trigger export (you'd need to expose this functionality)
          console.log('Export shortcut triggered');
        }
      }
      
      // ? - Show keyboard help
      if (e.key === '?' && !e.shiftKey) {
        e.preventDefault();
        setShowKeyboardHelp(!showKeyboardHelp);
      }
      
      // Tab navigation: 1, 2, 3
      if (e.key === '1' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setActiveTab(TABS.SETTINGS);
      }
      if (e.key === '2' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setActiveTab(TABS.AUDIO);
      }
      if (e.key === '3' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setActiveTab(TABS.LIVE);
      }
      if (e.key === '4' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setActiveTab(TABS.STORIES);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showKeyboardHelp, finalResults.length]);

  // Load sample audio files on mount
  useEffect(() => {
    const loadSampleAudio = async () => {
      try {
        const response = await fetch('/sample-audio/samples.json');
        if (!response.ok) {
          console.error('Failed to load samples.json');
          setIsLoadingSamples(false);
          setLoadingProgress(prev => ({ ...prev, samples: true }));
          return;
        }

        const samples = await response.json();
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        const loadedFiles = await Promise.all(
          samples.map(async (sample) => {
            try {
              // Fetch the audio file
              const audioResponse = await fetch(sample.file);
              if (!audioResponse.ok) throw new Error(`Failed to fetch ${sample.file}`);
              
              const audioBlob = await audioResponse.blob();
              const arrayBuffer = await audioBlob.arrayBuffer();
              const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
              
              // Determine content type from format
              let contentType = 'audio/wav';
              if (sample.format === 'mp3') contentType = 'audio/mp3';
              else if (sample.format === 'flac') contentType = 'audio/flac';
              else if (sample.format === 'ogg') contentType = 'audio/ogg';
              else if (sample.format === 'webm') contentType = 'audio/webm';
              
              return {
                id: sample.id,
                name: sample.label,
                language: sample.language,
                sampleRate: sample.sampleRate,
                duration: audioBuffer.duration,
                size: audioBlob.size,
                blob: audioBlob,
                url: URL.createObjectURL(audioBlob),
                contentType: contentType,
                isPreloaded: true
              };
            } catch (err) {
              console.error(`Failed to load sample: ${sample.label}`, err);
              return null;
            }
          })
        );

        // Filter out any failed loads
        const validFiles = loadedFiles.filter(file => file !== null);
        setAudioFiles(validFiles);
      } catch (err) {
        console.error('Error loading sample audio:', err);
      } finally {
        setIsLoadingSamples(false);
        setLoadingProgress(prev => ({ ...prev, samples: true }));
      }
    };

    loadSampleAudio();
  }, []);

  // Load stories from stories.json
  useEffect(() => {
    const loadStories = async () => {
      try {
        const response = await fetch('/stories.json');
        if (!response.ok) {
          console.error('Failed to load stories.json');
          setIsLoadingStories(false);
          setLoadingProgress(prev => ({ ...prev, stories: true }));
          return;
        }

        const data = await response.json();
        setStories(data.stories || []);
      } catch (err) {
        console.error('Error loading stories:', err);
      } finally {
        setIsLoadingStories(false);
        setLoadingProgress(prev => ({ ...prev, stories: true }));
      }
    };

    loadStories();
  }, []);

  // Check backend health on mount
  useEffect(() => {
    const checkBackendHealth = async () => {
      const proxyUrl = window.location.port === '3000' ? 'http://localhost:8080' : '';
      
      try {
        const response = await fetch(`${proxyUrl}/api/watson/health`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          console.log('✓ Backend is ready');
        } else {
          console.warn('⚠️ Backend responded but not healthy');
        }
      } catch (err) {
        console.warn('⚠️ Backend not reachable:', err.message);
      } finally {
        setLoadingProgress(prev => ({ ...prev, backend: true }));
      }
    };

    checkBackendHealth();
  }, []);

  // Check if all loading is complete
  useEffect(() => {
    const allLoaded = loadingProgress.backend && loadingProgress.samples && loadingProgress.stories;
    if (allLoaded && isAppLoading) {
      // Add a small delay for smooth transition
      setTimeout(() => {
        setIsAppLoading(false);
      }, 500);
    }
  }, [loadingProgress, isAppLoading]);

  const handleAddFile = (file) => {
    setAudioFiles(prev => [...prev, file]);
  };

  const handleDeleteFile = (fileId) => {
    setAudioFiles(prev => prev.filter(f => f.id !== fileId));
    if (selectedFile?.id === fileId) {
      setSelectedFile(null);
    }
  };

  const handleTranscribeFile = async () => {
    if (!selectedFile || !config.serviceUrl) return;

    // Clear previous results
    clearTranscripts();

    setIsTranscribing(true);
    const startTime = performance.now();

    try {
      const service = new WatsonSTTService(config.apiKey, config.serviceUrl);
      
      // Use the model from config
      const model = config.model;
      
      let audioBlob = selectedFile.blob;
      let contentType = selectedFile.contentType || 'audio/wav';
      
      if (selectedFile.isPreloaded && selectedFile.url) {
        const response = await fetch(selectedFile.url);
        audioBlob = await response.blob();
        contentType = audioBlob.type || 'audio/wav';
      }

      const params = {
        speech_detector_sensitivity: config.speech_detector_sensitivity,
        background_audio_suppression: config.background_audio_suppression,
        end_of_phrase_silence_time: config.end_of_phrase_silence_time,
        sad_module: config.sad_module,
        timestamps: config.timestamps,
        speaker_labels: config.speaker_labels,
        audio_metrics: config.audio_metrics,
        low_latency: config.low_latency,
        smart_formatting: config.smart_formatting,
        enrichments: config.enrichments,
        contentType: contentType
      };

      // Set transcription params for display
      updateTabResults({
        transcriptionParams: {
          model: model,
          ...params,
          status: 'processing'
        }
      });
      
      const result = await service.transcribeFile(audioBlob, model, params);
      
      const endTime = performance.now();
      const processingTime = ((endTime - startTime) / 1000).toFixed(2);
      
      // Calculate audio duration
      const audioDuration = selectedFile.duration || 0;
      const rtf = audioDuration > 0 ? (processingTime / audioDuration).toFixed(2) : 'N/A';
      
      console.log('Transcription result:', result);
      console.log('Checking for enriched results. enrichments config:', config.enrichments);
      console.log('enriched_results field:', result.enriched_results);
      console.log('enriched_result field:', result.enriched_result);
      
      // Update params with success status
      updateTabResults({
        transcriptionParams: {
          model: model,
          ...params,
          status: 'success',
          code: 200,
          code_description: 'OK'
        },
        rawResponse: result,
        performanceMetrics: {
          processingTime: processingTime,
          audioDuration: audioDuration.toFixed(2),
          realTimeFactor: rtf,
          timestamp: new Date().toISOString()
        }
      });
      
      // Extract final results
      if (result.results && result.results.length > 0) {
        console.log('Setting final results:', result.results);
        updateTabResults({ finalResults: result.results });
      } else {
        console.warn('No results found in response');
        updateTabResults({
          transcriptionError: {
            message: 'Transcription completed but no results were returned. The audio may be too short or contain no speech.',
            code: 200,
            code_description: 'OK - No Speech Detected'
          }
        });
      }

      // Extract speaker labels
      if (result.speaker_labels) {
        console.log('Setting speaker labels:', result.speaker_labels);
        updateTabResults({ speakerLabels: result.speaker_labels });
      }

      // Extract audio metrics
      if (result.audio_metrics) {
        updateTabResults({ audioMetrics: [result.audio_metrics] });
      }

      // Extract enriched results - they come in a single structure
      if (result.enriched_results && result.enriched_results.transcript) {
        const enrichedData = {
          transcript: result.enriched_results.transcript.text,
          timestamp: result.enriched_results.transcript.timestamp,
          status: result.enriched_results.status,
          enrichments: config.enrichments
        };
        
        console.log('Enriched data extracted:', enrichedData);
        updateTabResults({ enrichedResults: enrichedData });
      }
    } catch (err) {
      console.error('Transcription error:', err);
      
      // Extract error details
      let errorMessage = err.message || 'Unknown error occurred';
      let errorCode = null;
      let errorCodeDescription = null;
      
      // Check if error is already structured (from our API wrapper)
      if (err.error && err.code) {
        errorMessage = err.error;
        errorCode = err.code;
        errorCodeDescription = err.code_description;
      }
      
      // Update params with error status
      updateTabResults({
        transcriptionParams: {
          ...(tabResults[activeTab]?.transcriptionParams || {}),
          status: 'error',
          code: errorCode,
          code_description: errorCodeDescription
        },
        transcriptionError: {
          message: errorMessage,
          code: errorCode,
          code_description: errorCodeDescription
        }
      });
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleLiveTranscript = (results) => {
    const final = results.filter(r => r.final);
    const interim = results.filter(r => !r.final);
    
    // Update results by appending new ones to existing arrays
    setTabResults(prev => {
      const currentTab = prev[activeTab] || {};
      const updates = {};
      
      if (final.length > 0) {
        const newFinalResults = [...(currentTab.finalResults || []), ...final];
        updates.finalResults = newFinalResults;
      }
      
      // Accumulate interim results too (don't replace, append)
      if (interim.length > 0) {
        const newInterimResults = [...(currentTab.interimResults || []), ...interim];
        updates.interimResults = newInterimResults;
      }
      
      return {
        ...prev,
        [activeTab]: {
          ...currentTab,
          ...updates
        }
      };
    });
  };

  const handleLiveData = (data) => {
    if (!data) return;
    
    // Handle transcription results
    if (data.results && data.results.length > 0) {
      handleLiveTranscript(data.results);
    }

    // Handle speaker labels from WebSocket
    if (data.speaker_labels) {
      setTabResults(prev => ({
        ...prev,
        [activeTab]: {
          ...(prev[activeTab] || {}),
          speakerLabels: [...(prev[activeTab]?.speakerLabels || []), ...data.speaker_labels]
        }
      }));
    }

    // Handle audio metrics from WebSocket
    if (data.audio_metrics) {
      setTabResults(prev => ({
        ...prev,
        [activeTab]: {
          ...(prev[activeTab] || {}),
          audioMetrics: [...(prev[activeTab]?.audioMetrics || []), data.audio_metrics]
        }
      }));
    }

    // Handle processing metrics from WebSocket
    if (data.processing_metrics) {
      setTabResults(prev => ({
        ...prev,
        [activeTab]: {
          ...(prev[activeTab] || {}),
          processingMetrics: [...(prev[activeTab]?.processingMetrics || []), data.processing_metrics]
        }
      }));
    }

    // Handle enriched results from WebSocket - they come in a single structure
    if (data.enriched_results && data.enriched_results.transcript) {
      const enrichedData = {
        transcript: data.enriched_results.transcript.text,
        timestamp: data.enriched_results.transcript.timestamp,
        status: data.enriched_results.status,
        enrichments: config.enrichments
      };
      
      setTabResults(prev => ({
        ...prev,
        [activeTab]: {
          ...(prev[activeTab] || {}),
          enrichedResults: {
            ...(prev[activeTab]?.enrichedResults || {}),
            ...enrichedData
          }
        }
      }));
    }
  };

  const clearTranscripts = () => {
    updateTabResults({
      finalResults: [],
      interimResults: [],
      speakerLabels: [],
      audioMetrics: [],
      processingMetrics: [],
      enrichedResults: {},
      transcriptionParams: null,
      transcriptionError: null,
      rawResponse: null,
      performanceMetrics: null
    });
  };



  // Run story with model comparison
  const handleRunStory = async (story) => {
    if (!story || !story.compare_models || story.compare_models.length === 0) return;

    setIsRunningStory(true);
    setComparisonResults([]);
    
    // Determine if speaker labels should be enabled based on story metadata
    const speakerLabelsEnabled = story.multispeaker === true;

    try {
      // Fetch the audio file
      const audioResponse = await fetch(story.audio.file);
      if (!audioResponse.ok) {
        throw new Error(`Failed to fetch audio file: ${story.audio.file}`);
      }

      const audioBlob = await audioResponse.blob();
      const contentType = audioBlob.type || 'audio/wav';
      
      // Get proxy URL
      const proxyUrl = window.location.port === '3000' ? 'http://localhost:8080' : '';

      // Run transcription for each model in parallel
      // Stories always use us-east region
      const usEastRegion = REGIONS.find(r => r.code === 'us-east');
      const storyServiceUrl = usEastRegion ? usEastRegion.url : 'https://api.us-east.speech-to-text.watson.cloud.ibm.com';

      const promises = story.compare_models.map(async (modelConfig) => {
        const startTime = performance.now();
        
        try {
          // Build request parameters
          const params = {
            timestamps: true,
            speaker_labels: speakerLabelsEnabled,
            language_customization_id: modelConfig.language_customization_id,
            smart_formatting: story.smartformatting || false,
            smart_formatting_version: story.smartformatting_version
          };
          
          // Add enrichments if specified in story
          if (story.enrichment && story.enrichment !== 'none') {
            params.enrichments = story.enrichment;
            console.log(`🌟 Story enrichment config for ${modelConfig.name}:`, story.enrichment);
          }

          console.log('=== STORY API REQUEST (via proxy) ===');
          console.log('Model:', modelConfig.name);
          console.log('Region: US East (stories always use us-east)');
          console.log('Host:', new URL(storyServiceUrl).hostname);
          console.log('Customization ID:', modelConfig.language_customization_id || 'None');
          console.log('Speaker Labels:', speakerLabelsEnabled);
          console.log('Smart Formatting:', story.smartformatting || false, story.smartformatting_version ? `(v${story.smartformatting_version})` : '');
          console.log('Enrichment:', params.enrichments || 'none');
          console.log('=====================================');

          // Make the API call through proxy - use storyServiceUrl instead of modelConfig.service_url
          const response = await fetch(`${proxyUrl}/api/watson/transcribe`, {
            method: 'POST',
            headers: {
              'Content-Type': contentType,
              'x-service-url': storyServiceUrl,
              'x-model': modelConfig.model,
              'x-api-key': 'from-env', // Always use server-side credentials
              'x-params': JSON.stringify(params)
            },
            body: audioBlob
          });

          if (!response.ok) {
            const errorText = await response.text();
            let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            
            try {
              const errorJson = JSON.parse(errorText);
              if (errorJson.error) {
                errorMessage = errorJson.error;
              }
            } catch (e) {
              // Use default error message
            }
            
            throw new Error(errorMessage);
          }

          const result = await response.json();
          const endTime = performance.now();
          const processingTime = ((endTime - startTime) / 1000).toFixed(2);

          console.log('=== STORY API RESPONSE ===');
          console.log('Model:', modelConfig.name);
          console.log('Results count:', result.results?.length || 0);
          console.log('Speaker labels count:', result.speaker_labels?.length || 0);
          console.log('Enriched results:', result.enriched_results ? 'Present' : 'None');
          console.log('==========================');

          // Extract transcripts
          const transcripts = result.results || [];
          const speakerLabelsData = result.speaker_labels || [];
          
          // Extract enriched results if present
          let enrichedData = null;
          if (result.enriched_results && result.enriched_results.transcript) {
            enrichedData = {
              transcript: result.enriched_results.transcript.text,
              timestamp: result.enriched_results.transcript.timestamp,
              status: result.enriched_results.status,
              enrichments: story.enrichment ? [story.enrichment] : []
            };
          }
          
          // Count words
          const wordCount = transcripts.reduce((count, r) => {
            return count + (r.alternatives?.[0]?.timestamps?.length || 0);
          }, 0);

          return {
            modelName: modelConfig.name,
            isBaseModel: modelConfig.base_model,
            customizationId: modelConfig.language_customization_id,
            transcripts: transcripts,
            speakerLabels: speakerLabelsData,
            enrichedResults: enrichedData,
            processingTime: processingTime,
            wordCount: wordCount,
            error: null
          };
        } catch (err) {
          console.error(`Error transcribing with ${modelConfig.name}:`, err);
          const endTime = performance.now();
          const processingTime = ((endTime - startTime) / 1000).toFixed(2);
          
          return {
            modelName: modelConfig.name,
            isBaseModel: modelConfig.base_model,
            customizationId: modelConfig.language_customization_id,
            transcripts: [],
            speakerLabels: [],
            processingTime: processingTime,
            wordCount: 0,
            error: err.message
          };
        }
      });

      const results = await Promise.all(promises);
      setComparisonResults(results);
    } catch (err) {
      console.error('Error running story:', err);
      setComparisonResults([{
        modelName: 'Error',
        isBaseModel: true,
        transcripts: [],
        speakerLabels: [],
        error: err.message
      }]);
    } finally {
      setIsRunningStory(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onShowHelp={() => setShowKeyboardHelp(true)} />
      <TabNavigation 
        activeTab={activeTab} 
        onTabChange={setActiveTab}
        audioCount={audioFiles.length}
        storiesCount={stories.length}
      />

      {/* Keyboard shortcuts help modal */}
      {showKeyboardHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowKeyboardHelp(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md m-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">⌨️ Keyboard Shortcuts</h3>
              <button onClick={() => setShowKeyboardHelp(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Show this help</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">?</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Clear transcripts</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Ctrl+K</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Go to Audio Files</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">1</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Go to Live Recording</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">2</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Go to Settings</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">3</kbd>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`${activeTab === TABS.STORIES ? 'max-w-full' : 'max-w-7xl'} mx-auto px-4 py-6 sm:px-6 lg:px-8`}>
        {activeTab === TABS.SETTINGS ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ProductInfo />
            <SettingsTab config={config} onConfigChange={setConfig} showProductInfo={true} />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Main content area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left column - Input */}
              <div className="lg:col-span-2">
                {activeTab === TABS.AUDIO && (
                  <>
                    <AudioTab
                      audioFiles={audioFiles}
                      selectedFile={selectedFile}
                      onSelectFile={setSelectedFile}
                      onDeleteFile={handleDeleteFile}
                      onTranscribe={handleTranscribeFile}
                      isTranscribing={isTranscribing}
                      onAddFile={handleAddFile}
                      config={config}
                      isLoadingSamples={isLoadingSamples}
                    />
                    
                    {/* Clear button above final transcript */}
                    {(finalResults.length > 0 || interimResults.length > 0) && (
                      <div className="mt-4">
                        <button
                          onClick={clearTranscripts}
                          className="flex items-center space-x-1.5 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Clear All Results</span>
                        </button>
                      </div>
                    )}
                    
                    {/* Final transcript panel directly below transcribe button */}
                    {finalResults.length > 0 && (
                      <div className="mt-4">
                        <TranscriptPanel
                          title="Final Transcripts"
                          results={finalResults}
                          isFinal={true}
                          speakerLabels={speakerLabels}
                        />
                      </div>
                    )}
                    
                    {/* Interim Results - directly below Final if enabled */}
                    {config.interim_results && interimResults.length > 0 && (
                      <div className="mt-4">
                        <TranscriptPanel
                          title="Interim Results"
                          results={interimResults}
                          isFinal={false}
                          speakerLabels={speakerLabels}
                        />
                      </div>
                    )}
                    
                    {/* Enriched Results - directly below Interim (or Final if no interim) */}
                    {config.enrichments && Array.isArray(config.enrichments) && 
                     config.enrichments.length > 0 && !config.enrichments.includes('none') && 
                     enrichedResults.transcript && (
                      <div className="mt-4">
                        <EnrichedResultsPanel enrichedData={enrichedResults} />
                      </div>
                    )}
                  </>
                )}
                
                {activeTab === TABS.LIVE && (
                  <>
                    <LiveTab
                      config={config}
                      onAddFile={handleAddFile}
                      onLiveData={handleLiveData}
                      onStreamingStateChange={setIsLiveStreaming}
                      onSwitchTab={setActiveTab}
                      onSelectFile={setSelectedFile}
                      audioFiles={audioFiles}
                    />
                    
                    {/* Clear button above final transcript */}
                    {(finalResults.length > 0 || interimResults.length > 0) && (
                      <div className="mt-4">
                        <button
                          onClick={clearTranscripts}
                          className="flex items-center space-x-1.5 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Clear All Results</span>
                        </button>
                      </div>
                    )}
                    
                    {/* Final transcript panel directly below live recording */}
                    {finalResults.length > 0 && (
                      <div className="mt-4">
                        <TranscriptPanel
                          title="Final Transcripts"
                          results={finalResults}
                          isFinal={true}
                          speakerLabels={speakerLabels}
                        />
                      </div>
                    )}
                    
                    {/* Interim Results - directly below Final if enabled */}
                    {config.interim_results && interimResults.length > 0 && (
                      <div className="mt-4">
                        <TranscriptPanel
                          title="Interim Results"
                          results={interimResults}
                          isFinal={false}
                          speakerLabels={speakerLabels}
                        />
                      </div>
                    )}
                    
                    {/* Enriched Results - directly below Interim (or Final if no interim) */}
                    {config.enrichments && Array.isArray(config.enrichments) && 
                     config.enrichments.length > 0 && !config.enrichments.includes('none') && 
                     enrichedResults.transcript && (
                      <div className="mt-4">
                        <EnrichedResultsPanel enrichedData={enrichedResults} />
                      </div>
                    )}
                  </>
                )}

                {activeTab === TABS.STORIES && (
                  <StoriesTab
                    stories={stories}
                    selectedStory={selectedStory}
                    onSelectStory={setSelectedStory}
                    onRunStory={handleRunStory}
                    isRunning={isRunningStory}
                    config={config}
                    comparisonResults={comparisonResults}
                  />
                )}
              </div>

              {/* Right column - Quick access */}
              <div className="space-y-4">
                {activeTab !== TABS.STORIES && (
                  <>
                    <QuickSettingsPanel config={config} onConfigChange={setConfig} />
                  </>
                )}
                
                {transcriptionParams && (
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">📋 Transcription Details</h3>
                    
                    {/* Status Badge */}
                    {transcriptionParams.status && (
                      <div className="mb-3">
                        {transcriptionParams.status === 'success' && (
                          <div className="flex items-center space-x-2 bg-green-50 border border-green-200 rounded-lg p-2">
                            <Check className="w-4 h-4 text-green-600" />
                            <div>
                              <span className="text-sm font-semibold text-green-800">Success</span>
                              <div className="text-xs text-green-700">
                                Code {transcriptionParams.code}: {transcriptionParams.code_description}
                              </div>
                            </div>
                          </div>
                        )}
                        {transcriptionParams.status === 'error' && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-start space-x-2 mb-2">
                              <span className="text-red-600 text-lg">❌</span>
                              <div className="flex-1">
                                <h5 className="text-sm font-semibold text-red-900">Transcription Failed</h5>
                                {transcriptionParams.code && (
                                  <div className="text-xs text-red-700 mt-1">
                                    <span className="font-semibold">Error Code {transcriptionParams.code}</span>
                                    {transcriptionParams.code_description && (
                                      <span>: {transcriptionParams.code_description}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            {transcriptionError && (
                              <div className="mt-2 pt-2 border-t border-red-200">
                                <p className="text-xs font-medium text-red-900 mb-1">Error Details:</p>
                                <p className="text-xs text-red-800 whitespace-pre-wrap bg-red-100 p-2 rounded">{transcriptionError.message}</p>
                              </div>
                            )}
                          </div>
                        )}
                        {transcriptionParams.status === 'processing' && (
                          <div className="flex items-center space-x-2 bg-blue-50 border border-blue-200 rounded-lg p-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span className="text-sm font-semibold text-blue-800">Processing...</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Parameters */}
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-700">
                        <div><strong>Model:</strong> {transcriptionParams.model}</div>
                        <div><strong>Timestamps:</strong> {transcriptionParams.timestamps ? 'Yes' : 'No'}</div>
                        <div><strong>Speaker Labels:</strong> {transcriptionParams.speaker_labels ? 'Yes' : 'No'}</div>
                        <div><strong>Audio Metrics:</strong> {transcriptionParams.audio_metrics ? 'Yes' : 'No'}</div>
                        <div><strong>Low Latency:</strong> {transcriptionParams.low_latency ? 'Yes' : 'No'}</div>
                        {transcriptionParams.speech_detector_sensitivity !== '' && (
                          <div><strong>Speech Sensitivity:</strong> {transcriptionParams.speech_detector_sensitivity}</div>
                        )}
                        {transcriptionParams.background_audio_suppression !== '' && (
                          <div><strong>Background Suppression:</strong> {transcriptionParams.background_audio_suppression}</div>
                        )}
                        {transcriptionParams.end_of_phrase_silence_time !== '' && (
                          <div><strong>End of Phrase Silence:</strong> {transcriptionParams.end_of_phrase_silence_time}s</div>
                        )}
                        {transcriptionParams.sad_module && (
                          <div><strong>SAD Module:</strong> {transcriptionParams.sad_module}</div>
                        )}
                        {transcriptionParams.smart_formatting && transcriptionParams.smart_formatting !== 'none' && (
                          <div><strong>Smart Formatting:</strong> {transcriptionParams.smart_formatting}</div>
                        )}
                        {transcriptionParams.enrichments && Array.isArray(transcriptionParams.enrichments) && 
                         transcriptionParams.enrichments.length > 0 && !transcriptionParams.enrichments.includes('none') && (
                          <div className="col-span-2"><strong>Enrichments:</strong> {transcriptionParams.enrichments.join(', ')}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {(finalResults.length > 0 || isLiveStreaming) && activeTab === TABS.LIVE && (
                  <StatisticsPanel
                    results={finalResults}
                    audioMetrics={audioMetrics}
                    isLive={isLiveStreaming}
                  />
                )}
                
                {performanceMetrics && activeTab === TABS.LIVE && (
                  <PerformanceMetrics metrics={performanceMetrics} />
                )}
                
                {/* Processing Metrics Panel - for Live tab */}
                {config.processing_metrics && processingMetrics && processingMetrics.length > 0 && activeTab === TABS.LIVE && (
                  <ProcessingMetricsPanel processingMetrics={processingMetrics} />
                )}
                
                {/* Audio Metrics Panel - for Audio and Live tabs */}
                {config.audio_metrics && audioMetrics && audioMetrics.length > 0 && (activeTab === TABS.AUDIO || activeTab === TABS.LIVE) && (
                  <AudioMetricsPanel audioMetrics={audioMetrics} />
                )}
                
                {rawResponse && (
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">🔍 Raw API Response</h3>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(JSON.stringify(rawResponse, null, 2));
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Copy
                      </button>
                    </div>
                    <div className="bg-gray-50 rounded border border-gray-200 p-3 max-h-[400px] overflow-auto">
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">
                        {JSON.stringify(rawResponse, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>


          </div>
        )}
      </div>
    </div>
  );
}